import { SEND_READINESS_ID, SendReadinessChecklist, type ReadinessLocalIssue } from "./workspace-review/send-readiness";
import { RequestActivityDialog } from "./workspace-review/request-activity-dialog";
import { SolNextStep, SolDraftHistory, SolGuidance, solGuidanceItems, solHasCurrentResultWithoutDraft, type GuidanceTarget } from "./workspace-review/sol-guidance";
import { workspaceView, proposalOptions, preparationOptions } from "../api/workspace-view";
import type { WorkflowCommand } from "@/api";
import { RequestDetailsDialog, type RequestDetailsTarget } from "./workspace-review/request-details-dialog";
import { Menu } from "./workspace-review/menu";
import { WorkflowSwitch } from "./workspace-review/workflow-switch";
import { WorkflowCommercial } from "./workspace-review/workflow-panel";
// Live review context for one request. Every action uses the real core API; nothing is sent
// without an explicit, confirmed action.
import { DeleteMessageAction } from "./workspace-review/delete-message-action";
import React from "react";
import { useRouter } from "../router";
import { Banner, Button, Dialog, FormField, Textarea, Tooltip } from "@/ds";
import { fmtDate, Icon } from "@/lib";
import { roleAtLeast } from "@/lib/roles";
import { AssigneePicker } from "./assignee-picker";
import { ApiError, claimPrefetchedRequest, cockpit } from "@/api";
import { useLiveInvalidation } from "@/live/live-provider";
import type { DeliveryResolutionResult, Fact, FeatureKey, Flags, PositionOccasion, PositionProposalOptions, PositionStaffingFormat, RequestDetail } from "@/api";
import type { PushToast, ToastAction } from "@/notifications/toast-queue";
import { ownPrincipalKeys } from "../assignment-notifications";
import { useSession } from "../session";
import { readPrimaryScrollTop, writePrimaryScrollTop } from "../worklist-return-context";
import { useT } from "../i18n";
import {
  decisionSafetyNoticeKind,
  draftContinuationMessageKey,
  draftRevealEligible,
  draftWasOvertakenByNewerInbound,
  hasHamburgCutoffCustomerNotice,
  newerInboundRevisionPresent,
  newerMessageNoticeBodyKey,
  nextOpenConversationRoute,
  operatorRequestReferenceLabel,
  positionProposalHydration,
  proposalControlsDifferFromDraft,
  analysisRunInFlight,
  isDraftGenerationInProgress,
  type DraftEditorRefreshDraft,
} from "./screen-workspace.logic";
import { draftValueMarks, evidenceMarks } from "./evidence-highlight";
import { decisionActionSafetyModel, decisionSafetyApiErrorCode } from "./decision-action-safety";
import { ReplyAcknowledgementEffect, type ReplyAcknowledgementNotice } from "./reply-acknowledgement-effect";
import { reviewGuidanceModel } from "./review-guidance";
import { DeliveryFailureBanner } from "./delivery-failure";
import { DeliveryReconciliationPanel, activeDraftUncertainDelivery } from "./delivery-reconciliation";
import { pendingWorkspaceSubmission, readStoredSubmission, storeSubmission, submissionForPreview,
  submissionStorageKey, type WorkspaceSubmission } from "./workspace-submission";
import { EmailReviewWorkspace } from "./workspace-review/email-review-workspace";
import { PreviousRequests } from "./workspace-review/previous-requests";
import { FactItem, FactsPanel, ThreadOperations } from "./workspace-review/conversation-facts";
import { BankettProfiPanel, DraftPanel, PositionsPanel } from "./workspace-review/decision-offer";
import { ClientPanel } from "./workspace-review/customer-audit";
import { CustomerAddresses } from "./workspace-review/customer-addresses";
import { useWorkspaceHistory } from "./workspace-review/use-workspace-history";
import { useWorkspacePositions } from "./workspace-review/use-workspace-positions";
import { useWorkspaceActions } from "./workspace-review/use-workspace-actions";
import { useEvidenceSelection } from "./workspace-review/use-evidence-selection";
import { workspaceCorrespondence } from "./workspace-review/workspace-correspondence";
import { ReviewActions } from "./workspace-review/review-actions";
import { WorkspacePageHeading } from "./workspace-review/workspace-page-heading";
import { HamburgCutoffStatus } from "./workspace-review/hamburg-cutoff-status";
import reviewStyles from "./workspace-review/email-review.module.css";
import { reviewActionModel, reconcileReviewMessageSelection, type ReviewTabId } from "./workspace-review/model";
import { requestNotAssignedRefusal, type RequestNotAssignedRefusal } from "./request-not-assigned";

export interface WorkspaceScreenProps {
  requestId: string;
  flags?: Flags;
  /** #239: effective per-tenant feature toggles — hides surfaces the owner disabled (null =
   *  no tenant context; the API still enforces independently). */
  features?: Record<FeatureKey, boolean> | null;
  onBack: () => void;
  onTrashed?: (requestId: string) => void;
  backToEmailQa?: boolean;
  onOpenRequest: (requestId: string) => void;
  onGotoSettings?: () => void;
  onGotoCatalog?: () => void;
  onLocalAssignToMe?: (requestId: string) => void;
  pushToast: PushToast;
}

type Act = (
  p: Promise<unknown>,
  okTitle: string,
  okMsg?: React.ReactNode | ((result: unknown) => React.ReactNode),
  undo?: ToastAction,
) => Promise<boolean>;
type WorkspaceLoadOptions = { background?: boolean; live?: boolean; intentionalOpen?: boolean; revalidate?: boolean };

// ─────────────────────────────────────────────────────────────────────────────
function draftEditorRefreshDraft(draft: RequestDetail["draft"]): DraftEditorRefreshDraft {
  return draft
    ? { id: draft.id, revision: draft.revision, request_revision: draft.request_revision ?? null }
    : null;
}

export function WorkspaceScreen({
  requestId,
  flags,
  features,
  onBack,
  onTrashed,
  backToEmailQa,
  onOpenRequest,
  onGotoSettings,
  onGotoCatalog,
  onLocalAssignToMe,
  pushToast,
}: WorkspaceScreenProps) {
  const { user } = useSession();
  const { registerNavigationGuard } = useRouter();
  const t = useT();
  const tRef = React.useRef(t);
  const [data, setData] = React.useState<RequestDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [restoreBusy, setRestoreBusy] = React.useState(false);
  const [onlyRequiredFacts, setOnlyRequiredFacts] = React.useState(false);
  const [factEditors, setFactEditors] = React.useState<ReadonlySet<string>>(new Set());
  const trackFactEditing = React.useCallback((field: string, editing: boolean) => {
    setFactEditors(current => {
      if (current.has(field) === editing) return current;
      const next = new Set(current); if (editing) next.add(field); else next.delete(field); return next;
    });
  }, []);
  const workspaceKey = `${user?.id ?? ""}:${user?.tenantId ?? ""}:${requestId}`;
  const workspaceKeyRef = React.useRef(workspaceKey);
  workspaceKeyRef.current = workspaceKey;
  const scopeRef = React.useRef({ key: workspaceKey });
  if (scopeRef.current.key !== workspaceKey) scopeRef.current = { key: workspaceKey };
  const scope = scopeRef.current;
  const assessmentGuard = React.useMemo(() => ({ generation: 0, inFlight: 0 }), [scope]);
  const [assessmentRefreshRequired, setAssessmentRefreshRequired] = React.useState(false);
  const [detailRefreshFailed, setDetailRefreshFailed] = React.useState(false);
  const [retryConfirmation, setRetryConfirmation] = React.useState<string | null>(null);
  const retryCancelRef = React.useRef<HTMLButtonElement>(null);
  const retryInvokerRef = React.useRef<HTMLElement | null>(null);
  const [processError, setProcessError] = React.useState<string | null>(null);
  const [dataScope, setDataScope] = React.useState<typeof scope | null>(null);
  const [openingEntry, setOpeningEntry] = React.useState<{ key: string; id: string } | null>(null);
  const openingAttemptRef = React.useRef<object | null>(null);

  // Lifted draft-editor state (ReviewActions drives it from the bottom of the screen).
  const [editing, setEditing] = React.useState(false);
  const [recipientValue, setRecipientValue] = React.useState("");
  const [recipientValid, setRecipientValid] = React.useState(true);
  const [recipientFocus, setRecipientFocus] = React.useState(false);
  const [draftSaving, setDraftSaving] = React.useState(false);
  const [readinessTaskBusy, setReadinessTaskBusy] = React.useState(false);
  const [draftSubject, setDraftSubject] = React.useState("");
  const [draftBody, setDraftBody] = React.useState("");
  const [draftBodyHtml, setDraftBodyHtml] = React.useState("");
  const [draftBodyChanged, setDraftBodyChanged] = React.useState(false);
  const [editorStale, setEditorStale] = React.useState(false);
  const editorOwnedRef = React.useRef(false);
  editorOwnedRef.current = editing || editorStale || !!data?.draft && (draftBodyChanged
    || draftSubject !== (data.draft.subject || "") || recipientValue !== (data.draft.recipient?.effective_address ?? ""));
  const editorVersionRef = React.useRef<number | null>(null);
  const editorRequestRevisionRef = React.useRef<number | null>(null);
  const editorGenerationRef = React.useRef(0);
  const editorContentRef = React.useRef({ text: draftBody, html: draftBodyHtml });
  editorContentRef.current = { text: draftBody, html: draftBodyHtml };
  const editorSavedRef = React.useRef<number | null>(null);
  // #804: asked when the Draft panel actually presents a body — never on merely fetching the
  // request, so working in another tab does not spend the operator's one reveal. Stable, so the
  // reveal's effect keys on the draft rather than on a new function each render.
  const claimDraftFirstView = React.useCallback(
    (draftId: string) => cockpit.claimDraftFirstView(draftId).then((r) => r.claimed),
    [],
  );
  const [generating, setGenerating] = React.useState(false);
  const [recomputing, setRecomputing] = React.useState(false);
  const [analysisRetryBusy, setAnalysisRetryBusy] = React.useState(false);
  const [positionOccasion, setPositionOccasion] = React.useState<PositionOccasion | null>(null);
  const [positionStaffingFormat, setPositionStaffingFormat] = React.useState<PositionStaffingFormat | null>(null);
  const [positionDurationHours, setPositionDurationHours] = React.useState<number | null>(null);
  const [positionIncludeStaffing, setPositionIncludeStaffing] = React.useState(true);
  const {
    selectedMessageKey, activeMark, setSelectedMessageKey, setHoverMark, setPinnedMark,
    selectMessage, selectFactEvidence,
  } = useEvidenceSelection(data?.messages ?? []);
  const [activeReviewTab, setActiveReviewTab] = React.useState<ReviewTabId>("draft");
  const [revealDraftQuestions, setRevealDraftQuestions] = React.useState(false);
  const questionsRevealed = React.useCallback(() => setRevealDraftQuestions(false), []);
  const [activityScope, setActivityScope] = React.useState<string | null>(null);
  const [detailsTarget, setDetailsTarget] = React.useState<RequestDetailsTarget | null>(null);
  const requestMenuRef = React.useRef<HTMLButtonElement | null>(null);
  const detailsReturnRef = React.useRef<HTMLElement | null>(null);
  const [sendRecoveryState, setSendRecoveryState] = React.useState<"awaiting_authority" | "uncertain" | null>(null);
  const submissionRef = React.useRef<WorkspaceSubmission | null>(null);
  const preparationDirtyRef = React.useRef(false);
  const preparationBasisRef = React.useRef<number | null>(null);
  const preparationScopeRef = React.useRef<string | null>(null);
  const [pendingProcess, setPendingProcess] = React.useState<WorkflowCommand | null>(null);
  const processBusyRef = React.useRef(false);
  const positionProposalDraftRef = React.useRef<string | null | undefined>(undefined);
  const editingRef = React.useRef(false);
  const canonicalIdRef = React.useRef<string | null>(null);
  const {
    previousRequests, previousNextCursor, previousLoading, previousError,
    historicalData, historicalLoadingId, historicalSelectedMessageKey, historicalReviewTab,
    historicalMessages, historicalFacts,
    historicalMarks, historicalDraftMarks, historicalActiveMark,
    historicalDraftSender, historicalDraftRecipient,
    setHistoricalData, setHistoricalSelectedMessageKey, setHistoricalReviewTab, setHistoricalHoverMark,
    loadPreviousRequests, loadMorePreviousRequests, openPreviousRequestsPanel, openPreviousRequest,
    selectHistoricalFactEvidence,
  } = useWorkspaceHistory({ requestId, canonicalIdRef, editingRef, pushToast });
  const draftEditorSourceRef = React.useRef<DraftEditorRefreshDraft>(null);
  const workspace = data ? workspaceView(data) : null;
  const preparationStale = preparationDirtyRef.current && preparationBasisRef.current !== data?.request.current_revision;
  const markPreparationDirty = () => {
    if (!preparationDirtyRef.current) preparationBasisRef.current = data?.request.current_revision ?? null;
    preparationDirtyRef.current = true;
  };
  const singleAgent = workspace?.engine === "single_agent";
  const actionSafety = singleAgent ? { evidenceValid: Boolean(workspace?.current && !assessmentRefreshRequired && !detailRefreshFailed),
    decisionActionsBlocked: !workspace?.current || assessmentRefreshRequired || detailRefreshFailed, writerActionsBlocked: !data?.preparation.can_edit, reasonKeys: [], actionBlockers: [] }
    : decisionActionSafetyModel(data?.decision?.action_safety);
  const guidance = reviewGuidanceModel(data?.review_guidance);
  const workspaceEditing = editing && !actionSafety.writerActionsBlocked;
  const activeConflict = singleAgent && !pendingProcess && !generating ? workspace?.activeWorkflow?.conflict ?? null : null;
  const conflictAttemptId = activeConflict?.fields.length ? workspace?.activeWorkflow?.operation_id ?? null : null;
  const factsPanelRef = React.useRef<HTMLDivElement>(null);
  const revealedConflictAttempts = React.useMemo(() => new Set<string>(), [scope]);
  const focusConflictingFact = React.useCallback(() => {
    if (scopeRef.current !== scope) return;
    const row = factsPanelRef.current?.querySelector<HTMLElement>("[data-conflict]");
    if (!row) return;
    if (conflictAttemptId) revealedConflictAttempts.add(conflictAttemptId);
    row.scrollIntoView({ block: "nearest" });
    row.focus({ preventScroll: true });
  }, [scope, conflictAttemptId, revealedConflictAttempts]);
  const conflictFocusBlocked = editorOwnedRef.current || preparationDirtyRef.current || factEditors.size > 0;
  React.useEffect(() => {
    if (loading || error || dataScope !== scope || activeReviewTab !== "facts" || detailsTarget || historicalData
      || conflictFocusBlocked || !conflictAttemptId || revealedConflictAttempts.has(conflictAttemptId)) return;
    // A refusal owns one automatic reveal; refreshes and inline edits keep the operator's place.
    const frame = window.requestAnimationFrame(focusConflictingFact);
    return () => window.cancelAnimationFrame(frame);
  }, [loading, error, dataScope, scope, activeReviewTab, detailsTarget, historicalData,
    conflictFocusBlocked, conflictAttemptId, revealedConflictAttempts, focusConflictingFact]);
  const historicalBannerRef = React.useRef<HTMLDivElement>(null);
  const activeCustomerRef = React.useRef<HTMLDivElement>(null);
  const historyReturnRef = React.useRef<{
    requestId: string;
    previousRequestId: string;
    scrollTop: number;
  } | null>(null);
  const historicalRequestId = historicalData?.request.id ?? null;
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = historyReturnRef.current;
    if (saved?.requestId !== requestId) {
      historyReturnRef.current = null;
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      if (historicalRequestId) {
        historicalBannerRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
        historicalBannerRef.current?.querySelector("button")?.focus({ preventScroll: true });
      } else {
        const source = activeCustomerRef.current?.querySelector<HTMLButtonElement>(
          `[data-previous-request-id="${CSS.escape(saved.previousRequestId)}"]`,
        );
        source?.focus({ preventScroll: true });
        writePrimaryScrollTop(saved.scrollTop);
        historyReturnRef.current = null;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [historicalRequestId, requestId]);

  // The assignee picker uses the assignment-specific projection. Tenant admins receive members;
  // platform owners additionally receive all owners for the explicit cross-business override.
  const loadOperators = React.useCallback(() => cockpit.listAssignees(), []);
  const loadTeams = React.useCallback(() => cockpit.listTeams(), []);

  const [notAssigned, setNotAssigned] = React.useState<RequestNotAssignedRefusal | null>(null);

  React.useEffect(() => {
    tRef.current = t;
  }, [t]);

  // An undo offered by a toast can be clicked after the operator left the workspace (#536); the
  // action itself must still run, but reloading a screen nobody is looking at must not.
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  React.useEffect(() => {
    setDetailsTarget(null);
    setActivityScope(null);
    setReadinessTaskBusy(false);
  }, [workspaceKey]);

  React.useEffect(() => {
    positionProposalDraftRef.current = undefined;
    preparationDirtyRef.current = false;
    preparationBasisRef.current = null;
    preparationScopeRef.current = null;
    setPositionOccasion(null);
    setPositionStaffingFormat(null);
    setPositionDurationHours(null);
    setPositionIncludeStaffing(true);
  }, [requestId]);

  React.useEffect(() => {
    editingRef.current = workspaceEditing || factEditors.size > 0;
    if (actionSafety.writerActionsBlocked && editing) setEditing(false);
  }, [actionSafety.writerActionsBlocked, editing, workspaceEditing, factEditors.size]);

  React.useEffect(() => {
    if (factEditors.size === 0) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [factEditors.size]);
  React.useEffect(() => {
    if (factEditors.size === 0) return;
    return registerNavigationGuard(() => {
      pushToast({ tone: "warning", title: t("solFacts.finishEditing") });
      return false;
    });
  }, [factEditors.size, registerNavigationGuard, pushToast, t]);
  const guardFactEditor = (action: () => void) => {
    if (factEditors.size === 0) { action(); return; }
    pushToast({ tone: "warning", title: t("solFacts.finishEditing") });
    factsPanelRef.current?.querySelector<HTMLElement>('[data-testid="fact-inline-edit"] input, [data-testid="fact-inline-edit"] select')?.focus();
  };

  // `requestId` (from the URL) may be the readable request number; getRequest resolves it and
  // returns the canonical uuid. All actions below key off this uuid, never the URL identifier.
  const authoritativeDetailRef = React.useRef<RequestDetail | null>(null);
  const detailReadRef = React.useRef<{ scope: typeof scope; promise: Promise<boolean>; repeat: boolean; discard: boolean } | null>(null);
  const load: (opts?: WorkspaceLoadOptions) => Promise<boolean> = React.useCallback((opts?: WorkspaceLoadOptions) => {
    const background = opts?.background === true;
    const existing = detailReadRef.current;
    if (existing?.scope === scope) {
      // Invalidations/commands need one trailing read; a fallback tick only joins the read.
      const revalidate = opts?.revalidate || !opts?.live;
      if (revalidate) existing.repeat = true;
      // An explicit refresh after a save must not consume editor ownership with a
      // response started before the save. Live notifications only request catch-up.
      if (!opts?.live) existing.discard = true;
      // A revalidation can arrive after the read loop exits but before finally clears it.
      // Consume that late invalidation after settlement instead of joining a stale result.
      return existing.promise.then(applied => revalidate && existing.repeat && mounted.current && scopeRef.current === scope ? load(opts) : applied);
    }
    if (!background) setError(null);
    const read = { scope, repeat: false, discard: false, promise: Promise.resolve(false) };
    const applyDetail = (d: RequestDetail, intentionalOpen: boolean) => {
        if (!mounted.current || scopeRef.current !== scope) return false;
        if (authoritativeDetailRef.current && d.request.current_revision < authoritativeDetailRef.current.request.current_revision) return false;
        const previous = authoritativeDetailRef.current;
        if (previous && ((d.request.version ?? 0) < (previous.request.version ?? 0)
          || d.draft && previous.draft?.id === d.draft.id && d.draft.version < previous.draft.version)) return false;
        const nextDraftSource = draftEditorRefreshDraft(d.draft);
        // A save only releases the submitted editor generation; later typing remains locally owned.
        const resetDraftEditor = !editorOwnedRef.current || intentionalOpen
          || editorSavedRef.current === editorGenerationRef.current;
        editorSavedRef.current = null;
        if (!resetDraftEditor) setEditorStale(draftEditorSourceRef.current?.id !== d.draft?.id
          || editorVersionRef.current !== d.draft?.version
          || editorRequestRevisionRef.current !== d.request.current_revision
          || draftEditorSourceRef.current?.request_revision !== (d.draft?.request_revision ?? null));
        canonicalIdRef.current = d.request.id;
        authoritativeDetailRef.current = d;
        if (["SENT", "UNKNOWN", "FAILED_BEFORE_SUBMISSION"].includes(d.delivery?.state ?? "")) {
          const storageKey = submissionStorageKey(user?.id ?? "", d.request.id);
          const stored = submissionRef.current ?? readStoredSubmission(storageKey);
          if (stored?.command.idempotency_key === d.delivery?.operatorCommandKey) {
            submissionRef.current = null;
            storeSubmission(storageKey, null);
          }
        }
        setData(d);
        setDataScope(scope);
        setDetailRefreshFailed(false);
        const authoritativeGuidance = reviewGuidanceModel(d.review_guidance);
        const authoritativeSendBlocked = authoritativeGuidance.state !== "ready"
          || authoritativeGuidance.next_action.code !== "SEND_DRAFT";
        setSendRecoveryState((current) => current && authoritativeSendBlocked ? null : current);
        setSelectedMessageKey((current) => reconcileReviewMessageSelection(d.messages, current));
        const proposalHydration = positionProposalHydration(
          positionProposalDraftRef.current,
          d.draft,
        );
        if (proposalHydration && resetDraftEditor && !preparationDirtyRef.current && d.request.engine_pin.engine === "deterministic") {
          positionProposalDraftRef.current = proposalHydration.draftId;
          setPositionOccasion(proposalHydration.occasion);
          setPositionStaffingFormat(proposalHydration.staffingFormat);
          setPositionDurationHours(proposalHydration.durationHours);
          setPositionIncludeStaffing(proposalHydration.includeStaffing);
        }
        let pending: WorkflowCommand | null = null;
        try { pending = JSON.parse(sessionStorage.getItem(`reqventos:workspace-command:${user?.id}:${d.request.id}`) ?? "null"); } catch { /* storage unavailable */ }
        if (pending && pending.expected_engine_epoch !== d.request.engine_pin.engine_epoch) {
          sessionStorage.removeItem(`reqventos:workspace-command:${user?.id}:${d.request.id}`);
          pending = null;
        }
        setPendingProcess(pending);
        const preparationScope = `${d.request.id}:${d.request.engine_pin.engine_epoch}`;
        if (preparationScopeRef.current !== preparationScope || !preparationDirtyRef.current && (d.preparation.saved || d.request.engine_pin.engine === "single_agent")) {
          preparationScopeRef.current = preparationScope;
          preparationDirtyRef.current = false;
          let options = proposalOptions(d.preparation.options);
          if (d.request.engine_pin.engine === "deterministic" && !d.preparation.saved) options = positionProposalHydration(undefined, d.draft) ?? options;
          if (d.request.engine_pin.engine === "deterministic" && !d.preparation.saved && d.draft?.offer_snapshot) options.occasion = d.draft.offer_snapshot.occasion;
          setPositionOccasion(options.occasion); setPositionStaffingFormat(options.staffingFormat);
          setPositionDurationHours(options.durationHours); setPositionIncludeStaffing(options.includeStaffing);
        }
        if (pending?.preparation) {
          const options = proposalOptions(pending.preparation);
          setPositionOccasion(options.occasion); setPositionStaffingFormat(options.staffingFormat);
          setPositionDurationHours(options.durationHours); setPositionIncludeStaffing(options.includeStaffing);
        }
        if (resetDraftEditor) {
          setEditorStale(false);
          editorVersionRef.current = d.draft?.version ?? null;
          editorRequestRevisionRef.current = d.request.current_revision;
          draftEditorSourceRef.current = nextDraftSource;
          setDraftSubject(d.draft?.subject || "");
          setRecipientValue(d.draft?.recipient?.effective_address ?? "");
          setRecipientValid(true);
          setRecipientFocus(false);
          setDraftBody(d.draft?.reply_body_text ?? d.draft?.body_text ?? "");
          setDraftBodyHtml(d.draft?.reply_body_html ?? d.draft?.body_html ?? "");
          setDraftBodyChanged(false);
          setEditing(false);
        }
        if (intentionalOpen) setOpeningEntry({ key: workspaceKey, id: d.request.id });
        setLoading(false);
        return true;
    };
    read.promise = (async () => {
      let applied = false;
      let reads = 0;
      do {
        reads++;
        read.repeat = false;
        read.discard = false;
        const assessmentGeneration = assessmentGuard.generation;
        // #2302: an opening takes over the read its row press started; any later read is its own.
        const prefetched = !applied && opts?.intentionalOpen === true ? claimPrefetchedRequest(requestId) : null;
        let d = await (prefetched ? prefetched.catch(() => cockpit.getRequest(requestId)) : cockpit.getRequest(requestId));
        if (!mounted.current || scopeRef.current !== scope) return false;
        // A lost process response is reconciled by reading its persisted command, never by
        // automatically submitting again. Read detail after confirmation to hydrate saved options.
        const commandStorageKey = `reqventos:workspace-command:${user?.id}:${d.request.id}`;
        let pending: WorkflowCommand | null = null;
        try { pending = JSON.parse(sessionStorage.getItem(commandStorageKey) ?? "null"); } catch { /* storage unavailable */ }
        if (pending && !processBusyRef.current) {
          const status = await cockpit.getWorkflowProcessStatus(d.request.id, pending.idempotency_key);
          if (!mounted.current || scopeRef.current !== scope) return false;
          if (status.accepted) {
            sessionStorage.removeItem(commandStorageKey);
            preparationDirtyRef.current = false;
            setPendingProcess(null); setProcessError(null);
            d = await cockpit.getRequest(requestId);
          }
        }
        // Live notifications may concern other requests. Publish useful reads, but never
        // let a read started before a command settled release its editor ownership.
        if (!read.discard && assessmentGeneration === assessmentGuard.generation) {
          const accepted = applyDetail(d, !applied && opts?.intentionalOpen === true);
          if (accepted && assessmentGeneration === assessmentGuard.generation && assessmentGuard.inFlight === 0) setAssessmentRefreshRequired(false);
          applied = accepted || applied;
        }
      } while (read.repeat && reads < 2 && mounted.current && scopeRef.current === scope);
      return applied;
    })()
      .catch((e) => {
        if (mounted.current && scopeRef.current === scope) {
          setDetailRefreshFailed(true);
          if (!authoritativeDetailRef.current) setError(tRef.current("workspace.readFailed"));
        }
        return false;
      })
      .finally(() => {
        if (detailReadRef.current === read) detailReadRef.current = null;
        if (mounted.current && scopeRef.current === scope) setLoading(false);
        const repeat = read.repeat;
        read.repeat = false;
        // Continuous notifications must not keep a save awaiting this promise forever.
        if (repeat && mounted.current && scopeRef.current === scope)
          queueMicrotask(() => { if (mounted.current && scopeRef.current === scope) void load({ background: true, live: true }); });
      });
    detailReadRef.current = read;
    return read.promise;
  }, [requestId, user?.id, workspaceKey, scope, assessmentGuard]);

  React.useEffect(() => {
    setLoading(true);
    setEditing(false);
    editorOwnedRef.current = false;
    editorSavedRef.current = null;
    setEditorStale(false);
    canonicalIdRef.current = null;
    setSelectedMessageKey(null);
    setActiveReviewTab("draft");
    setRevealDraftQuestions(false);
    setSendRecoveryState(null);
    setDetailRefreshFailed(false); setProcessError(null); setPendingProcess(null);
    setGenerating(false); processBusyRef.current = false; setRetryConfirmation(null);
    submissionRef.current = null;
    authoritativeDetailRef.current = null;
    draftEditorSourceRef.current = null;
    setOpeningEntry(null);
    void load({ intentionalOpen: true });
  }, [load]);

  // Only a successfully rendered, visible workspace entry records opening. Background loads,
  // historical previews and owner support viewing never create this action. The server owns
  // team eligibility and the atomic first-writer check; this guard only avoids stray UI calls.
  React.useEffect(() => {
    if (!openingEntry || openingEntry.key !== workspaceKey || loading
      || data?.request.id !== openingEntry.id || user?.tenantStatus !== "active"
      || (user?.role !== "admin" && user?.role !== "operator")) return;
    let active = true;
    const record = () => {
      if (document.visibilityState !== "visible" || openingAttemptRef.current === openingEntry) return;
      openingAttemptRef.current = openingEntry;
      void cockpit.recordRequestOpening(openingEntry.id).then(() => {
        if (!active || workspaceKeyRef.current !== openingEntry.key) return;
        setData((current) => current?.request.id === openingEntry.id
          ? { ...current, request: { ...current.request, opening_state: "opened" } }
          : current);
      }).catch(() => {
        if (active && workspaceKeyRef.current === openingEntry.key) {
          pushToast({ tone: "danger", title: tRef.current("ws.openingFailed") });
        }
      });
    };
    record();
    document.addEventListener("visibilitychange", record);
    return () => { active = false; document.removeEventListener("visibilitychange", record); };
  }, [openingEntry, workspaceKey, loading, data?.request.id, user?.role, user?.tenantStatus, pushToast]);

  // Everything this screen renders: the request itself, its messages/facts/draft, the customer
  // card, and the analysis state (#544).
  const invalidations = useLiveInvalidation("workspace", "requests", "customers", "pipeline", "catalog", "settings", "tenants");
  const acknowledgedViews = React.useRef(new Set<string>());
  const acknowledgeDisplayedDraft = React.useCallback(() => {
    const work = data?.incoming_work;
    const identity = work?.readiness_id;
    if (!data || !identity || work?.state !== "ready_unseen" || work.draft_id !== data.draft?.id
        || data.draft?.is_current_decision !== true || editorOwnedRef.current
        || workspaceKeyRef.current !== workspaceKey || user?.tenantStatus !== "active"
        || !["operator", "admin"].includes(user?.role ?? "") || acknowledgedViews.current.has(identity)) return;
    acknowledgedViews.current.add(identity);
    void cockpit.acknowledgeCurrentDraft(data.request.id, identity).then(() => {
      if (workspaceKeyRef.current === workspaceKey) void load({ background: true });
    }).catch(() => {
      acknowledgedViews.current.delete(identity);
      if (workspaceKeyRef.current === workspaceKey) pushToast({ tone: "warning", title: tRef.current("incoming.viewFailed") });
    });
  }, [data, workspaceKey, user?.role, user?.tenantStatus, load, pushToast]);
  const seenInvalidations = React.useRef({ scope, value: invalidations });

  React.useEffect(() => {
    const previous = seenInvalidations.current;
    seenInvalidations.current = { scope, value: invalidations };
    if (previous.scope !== scope || previous.value === invalidations) return;
    void load({ background: true, live: true, revalidate: true });
  }, [invalidations, load, scope]);

  const fallbackKey = `${workspaceKey}:${data?.incoming_work?.inbound_basis ?? ""}:${data?.workflow?.processing?.started_at ?? data?.workflow?.operation_id ?? ""}`;
  const fallbackReads = React.useRef({ key: fallbackKey, count: 0 });
  if (fallbackReads.current.key !== fallbackKey) fallbackReads.current = { key: fallbackKey, count: 0 };
  React.useEffect(() => {
    if (data?.incoming_work?.state !== "processing" && !data?.workflow?.recalculation_waiting) return;
    // At most 60 visible fallback reads per operation. Live events remain authoritative.
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible" && fallbackReads.current.count < 60) {
        fallbackReads.current.count += 1;
        void load({ background: true, live: true });
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [data?.incoming_work?.state, data?.workflow?.recalculation_waiting, load]);

  const positionsRead = useWorkspacePositions(data, scope, dataScope === scope && features?.positions_offer !== false);

  const acknowledgeRenderedReply = React.useCallback((id: string, token: string) => (
    cockpit.acknowledgeReply(id, token)
  ), []);
  const reloadAfterReplyAcknowledgement = React.useCallback(() => (
    load({ background: true, live: true })
  ), [load]);
  const handleReplyAcknowledgementNotice = React.useCallback((
    notice: ReplyAcknowledgementNotice,
    error?: unknown,
  ) => {
    if (notice === "newer_reply_available") {
      pushToast({ tone: "warning", title: t("ws.replyAcknowledgementRace") });
      return;
    }
    const detail = error instanceof Error ? error.message : undefined;
    pushToast({
      tone: "danger",
      title: t(notice === "reload_failed"
        ? "ws.replyAcknowledgementReloadFailed"
        : "ws.replyAcknowledgementFailed"),
      msg: detail,
    });
  }, [pushToast, t]);

  const handleDecisionSafetyError = React.useCallback(async (error: unknown) => {
    if (!decisionSafetyApiErrorCode(error)) return false;
    const reloaded = await load({ background: true });
    pushToast({
      tone: "danger",
      title: t("ws.decisionSafetyTitle"),
      msg: t(reloaded ? "ws.decisionSafetyReloaded" : "ws.decisionSafetyReloadFailed"),
    });
    return true;
  }, [load, pushToast, t]);

  // #803: the refusal is informational. There is nothing the operator can do from here — an
  // admin decides who works on a request — so the dialog says so and closes.
  const handleNotAssigned = React.useCallback((error: unknown) => {
    const refusal = requestNotAssignedRefusal(error);
    if (!refusal) return false;
    setNotAssigned(refusal);
    return true;
  }, []);

  const act = React.useCallback<Act>(async (p, okTitle, okMsg, undo) => {
    // A committed or uncertain mutation invalidates the old read assessment. Only a read
    // started after settlement may restore it, including after a failed immediate refresh.
    if (singleAgent) {
      assessmentGuard.generation++; assessmentGuard.inFlight++;
      setAssessmentRefreshRequired(true);
    }
    const settled = p.finally(() => {
      if (singleAgent) { assessmentGuard.generation++; assessmentGuard.inFlight--; }
    });
    try {
      const result = await settled;
      pushToast({
        tone: "success",
        title: okTitle,
        msg: typeof okMsg === "function" ? okMsg(result) : okMsg,
        undo,
      });
      if (mounted.current) await load();
      return true;
    } catch (e: any) {
      if (handleNotAssigned(e)) {
        if (singleAgent && mounted.current) await load({ background: true });
        return false;
      }
      if (await handleDecisionSafetyError(e)) return false;
      pushToast({ tone: "danger", title: t("common.error"), msg: e instanceof ApiError && (e.data as { code?: string })?.code === "DRAFT_RECIPIENT_INVALID"
        ? t("ws.recipient.invalid") : e instanceof ApiError && (e.data as { code?: string })?.code === "FORM_ORIGIN_IMMUTABLE"
        ? t("ws.form.originImmutable") : e instanceof ApiError && (e.data as { code?: string })?.code === "APPROVER_NAME_REQUIRED"
        ? t("ws.approverNameRequiredBody") : e instanceof ApiError && (e.data as { code?: string })?.code === "DRAFT_CLOSING_REGENERATION_REQUIRED"
        ? t("ws.closingRegenerationRequiredBody") : e?.message || String(e) });
      if (mounted.current) await load({ background: true });
      return false;
    }
  }, [handleDecisionSafetyError, handleNotAssigned, load, pushToast, t, singleAgent, assessmentGuard]);

  const retryKey = JSON.stringify([scope, data?.request.current_revision, data?.request.engine_pin,
    data?.workflow?.refusal, data?.workflow?.reason, data?.workflow?.result_id, data?.workflow?.receipt?.known_micro_usd,
    preparationDirtyRef.current, positionOccasion, positionStaffingFormat, positionDurationHours, positionIncludeStaffing]);
  const generateDraft = React.useCallback(async (confirmedRetry?: string) => {
    const rid = canonicalIdRef.current;
    if (
      !rid
      || (singleAgent ? !data?.workflow?.can_process || !data.preparation.can_edit : actionSafety.decisionActionsBlocked)
      || preparationStale && !pendingProcess
      || detailRefreshFailed || assessmentRefreshRequired || positionsRead.failed
      || factEditors.size > 0
      || processBusyRef.current
      || isDraftGenerationInProgress(data?.request.status, generating)
    ) return;
    // Processing again is paid and replaces the result its confirmations belong to (#2516), so it
    // says so first, just as a retry after a refusal does.
    if (singleAgent && (data?.workflow?.refusal || data?.workflow?.result_id) && !pendingProcess && confirmedRetry !== retryKey) {
      retryInvokerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setRetryConfirmation(retryKey);
      return;
    }
    setRetryConfirmation(null);
    processBusyRef.current = true;
    setGenerating(true);
    setProcessError(null);
    const commandScope = scope;
    const stillHere = () => mounted.current && scopeRef.current === commandScope;
    if (singleAgent) {
      assessmentGuard.generation++; assessmentGuard.inFlight++;
      setAssessmentRefreshRequired(true);
    }
    let safetyErrorHandled = false;
    let recovering = false;
    try {
      if (singleAgent && data) {
        const storageKey = `reqventos:workspace-command:${user?.id}:${rid}`;
        let command: WorkflowCommand | null = null;
        try { command = JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as WorkflowCommand | null; } catch { /* no retained command */ }
        recovering = Boolean(command);
        if (command) {
          const status = await cockpit.getWorkflowProcessStatus(rid, command.idempotency_key);
          if (!stillHere()) return;
          if (status.accepted) {
            sessionStorage.removeItem(storageKey);
            setPendingProcess(null); preparationDirtyRef.current = false;
            return;
          }
        }
        command ??= { expected_revision: data.request.current_revision,
          expected_engine_epoch: data.request.engine_pin.engine_epoch, idempotency_key: crypto.randomUUID(),
          ...(preparationDirtyRef.current ? { preparation: preparationOptions({ occasion: positionOccasion, staffingFormat: positionStaffingFormat,
            durationHours: positionDurationHours, includeStaffing: positionIncludeStaffing }) } : {}) };
        sessionStorage.setItem(storageKey, JSON.stringify(command));
        setPendingProcess(command);
        await cockpit.processWorkflow(rid, command);
        if (!stillHere()) return;
        sessionStorage.removeItem(storageKey);
        setPendingProcess(null);
        preparationDirtyRef.current = false;
        pushToast({ tone: "info", title: t("workflow.state.queued") });
        return;
      }
      const result = await cockpit.generateDraft(rid, {
        occasion: positionOccasion,
        staffingFormat: positionStaffingFormat,
        durationHours: positionDurationHours,
        includeStaffing: positionIncludeStaffing,
      }, preparationDirtyRef.current ? data?.request.current_revision : undefined);
      if (!stillHere()) return;
      if (result.status === "drafted") preparationDirtyRef.current = false;
      pushToast(result.status === "drafted"
        ? { tone: "success", title: t("ws.draftCreated") }
        : result.status === "skipped_human_review"
          ? { tone: "warning", title: t("ws.skipped"), msg: t("ws.humanReviewRequired") }
          : result.status === "skipped_commercial_defer"
            ? { tone: "warning", title: t("ws.skipped"), msg: t("ws.autoDraftCommercialDefer") }
          : { tone: "danger", title: t("ws.resultPrefix") + result.status, msg: result.error });
    } catch (error: any) {
      if (!stillHere()) return;
      // A first refusal can be corrected. A retained command is releasable only after a
      // stale-basis refusal under the request lock: its old revision/epoch cannot commit
      // later. Other refusals cannot rule out an earlier request still committing.
      const obsoleteCommand = error instanceof ApiError && error.status === 409
        && (error.data as { code?: string })?.code === "request_basis_stale";
      if (singleAgent && (!recovering || obsoleteCommand) && error?.status >= 400 && error?.status < 500) {
        const storageKey = `reqventos:workspace-command:${user?.id}:${rid}`;
        try {
          const command = JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as WorkflowCommand | null;
          const status = command && await cockpit.getWorkflowProcessStatus(rid, command.idempotency_key);
          if (!stillHere()) return;
          if (status?.accepted) preparationDirtyRef.current = false;
          sessionStorage.removeItem(storageKey); setPendingProcess(null);
        } catch { /* Keep the uncertain command until a read can reconcile it. */ }
      }
      if (!stillHere()) return;
      setProcessError(error?.name === "TimeoutError" ? t("workspace.recoverSubmissionHint") : error?.message || t("workflow.error.generic"));
      if (handleNotAssigned(error)) return;
      safetyErrorHandled = await handleDecisionSafetyError(error);
      if (!safetyErrorHandled) {
        pushToast({ tone: "danger", title: t("common.error"), msg: error?.message || String(error) });
      }
    } finally {
      if (singleAgent) { assessmentGuard.generation++; assessmentGuard.inFlight--; }
      if (stillHere()) {
        processBusyRef.current = false;
        setGenerating(false);
        if (singleAgent || !safetyErrorHandled) void load({ background: true });
      }
    }
  }, [
    actionSafety.decisionActionsBlocked,
    singleAgent, data, user?.id, preparationStale, pendingProcess, scope, assessmentGuard, retryKey,
    detailRefreshFailed, assessmentRefreshRequired, positionsRead.failed, factEditors.size,
    data?.request.status,
    generating,
    handleDecisionSafetyError,
    handleNotAssigned,
    load,
    positionOccasion,
    positionStaffingFormat,
    positionDurationHours,
    positionIncludeStaffing,
    pushToast,
    t,
  ]);

  const recomputeDecision = React.useCallback(async () => {
    if (singleAgent) { await generateDraft(); return; }
    const rid = canonicalIdRef.current;
    if (
      !rid ||
      recomputing ||
      guidance.next_action.code !== "RECOMPUTE_DECISION"
    ) return;
    setRecomputing(true);
    try {
      const result = await cockpit.recomputeDecision(rid);
      const reloaded = await load({ background: true });
      pushToast(reloaded
        ? {
            tone: "success",
            title: t("ws.recomputeSuccess"),
            msg: t(draftContinuationMessageKey(result.draft_status)),
          }
        : { tone: "danger", title: t("ws.recomputeReloadFailed") });
    } catch (error: any) {
      if (handleNotAssigned(error)) return;
      if (!await handleDecisionSafetyError(error)) {
        pushToast({
          tone: "danger",
          title: t("ws.recomputeFailed"),
          msg: error?.message || String(error),
        });
      }
    } finally {
      setRecomputing(false);
    }
  }, [
    guidance.next_action.code, singleAgent, generateDraft,
    handleDecisionSafetyError,
    handleNotAssigned,
    load,
    pushToast,
    recomputing,
    t,
  ]);

  const {
    assignees,
    closeOpen, closeReason, closeBusy, closeError,
    setCloseOpen, setCloseReason, setCloseError,
    openMarkDone, submitMarkDone,
    trashOpen, trashBusy, trashError, setTrashOpen, openTrash, submitTrash, restoreFromTrash,
    addAssignee, removeAssignee,
  } = useWorkspaceActions({
    request: data?.request ?? null,
    userId: user?.id,
    onLocalSelfAssign: onLocalAssignToMe,
    setData,
    pushToast,
    onBack,
    onTrashed,
    handleNotAssigned,
    act,
  });

  if (loading || dataScope !== scope && !error) {
    // Busy, so the page settle (#1543) holds it back while the read finishes within its limit (#2302).
    return <div className="ws"><div className="center-screen"><p className="subtle" role="status" aria-busy="true">{t("ws.loadingRequest")}</p></div></div>;
  }
  if (error || !data) {
    return (
      <div className="ws">
        <div className="row" style={{ gap: "var(--space-2)", marginBottom: "var(--space-4)" }}>
          <button className="iconbtn" aria-label={t("ws.back")} onClick={onBack}><Icon name="arrowLeft" size={16} /></button>
        </div>
        <Banner tone="danger" title={t("ws.requestUnavailable")}>
          {error || t("ws.unknownError")}{" "}
          <button className="linkbtn" onClick={() => { setLoading(true); load({ intentionalOpen: true }); }}>{t("common.retry")}</button>
        </Banner>
      </div>
    );
  }

  const { request, messages, facts, decision, draft } = data;
  // #376: evidence marks — the customer's quotes (thread) and the facts' values (draft).
  const marks = evidenceMarks(facts);
  const draftMarks = draftValueMarks(facts);
  const uncertainDelivery = activeDraftUncertainDelivery(data);
  const onDeliveryResolved = (result: DeliveryResolutionResult) => {
    setSendRecoveryState(null);
    pushToast({
      tone: "success",
      title: t(result.resolved === "delivered"
        ? "delivery.success.delivered"
        : "delivery.success.notDelivered"),
    });
    void load({ background: true });
  };
  const onDeliveryStale = () => {
    pushToast({ tone: "warning", title: t("delivery.error.stale") });
    return load({ background: true }).then(() => undefined);
  };
  const draftGenerating = isDraftGenerationInProgress(request.status, generating);
  const canEditRecipient = reviewActionModel({
    hasDraft: Boolean(draft), reviewState: draft?.review_state ?? "pending",
    guidanceState: guidance.state, nextAction: guidance.next_action.code,
    proposalControlsChanged: false, editing: workspaceEditing, humanReview: false,
    decisionActionsBlocked: actionSafety.decisionActionsBlocked,
    writerActionsBlocked: actionSafety.writerActionsBlocked,
    stale: guidance.primary_blocker?.code === "STALE_DRAFT"
      || guidance.additional_blockers.some((item) => item.code === "STALE_DRAFT"),
    terminal: guidance.state === "complete" || ["sent", "closed"].includes(request.status),
    reconciliationPending: sendRecoveryState !== null
      || ["RECONCILE_DELIVERY", "CONTINUE_SUBMISSION"].includes(guidance.next_action.code)
      || ["SENDING", "UNKNOWN", "IDLE"].includes(data.delivery?.state ?? ""),
  }).canEdit;
  const draftDirty = !!draft
    && (draftSubject !== (draft.subject || "") || draftBodyChanged
      || recipientValue !== (draft.recipient?.effective_address ?? ""));
  const proposalControlsChanged = request.status !== "sent"
    && request.status !== "closed"
    && (singleAgent ? preparationDirtyRef.current || Boolean(pendingProcess) : proposalControlsDifferFromDraft(draft, {
      occasion: positionOccasion,
      staffingFormat: positionStaffingFormat,
      durationHours: positionDurationHours,
      includeStaffing: positionIncludeStaffing,
    }));
  const requestReference = operatorRequestReferenceLabel(request.reference);
  // Short readable request number shown in the header and mirrored in the URL (e.g. "#1042").
  const requestNumber = request.request_no != null ? `#${request.request_no}` : null;
  const { company, requestSubject, draftSender, draftRecipient } = workspaceCorrespondence(facts, messages, t);
  const formCustomerName = ["customer.company_name", "customer.contact_name"]
    .map((path) => facts.find((fact) => fact.fieldPath === path
      && ["extracted", "confirmed", "manually_overridden", "externally_resolved",
        "curated_venue_resolved"].includes(fact.status))?.value)
    .find((value): value is string => typeof value === "string" && value.trim().length > 0);
  const isAdmin = roleAtLeast(user?.role ?? "", "admin");
  const isClosed = request.status === "closed";
  // #1787: a trashed request is read-only until restored; the server refuses any work on it.
  const inTrash = Boolean(request.trashed_at);
  const restoreRequest = async () => {
    if (typeof request.version !== "number") return;
    setRestoreBusy(true);
    try {
      if (await restoreFromTrash(request.id, request.version)) await load();
      else await load({ background: true });
    } finally {
      setRestoreBusy(false);
    }
  };
  const positionsCurrent = positionsRead.positions;
  const positionsDisplayLoading = positionsRead.loading;
  const refreshPositions = () => { void load({ background: true }); positionsRead.refresh(); };
  const positionState = detailRefreshFailed || positionsRead.failed ? "read_failed"
    : generating ? "submitting" : pendingProcess ? "uncertain"
    : singleAgent && workspace?.processing?.in_progress ? "processing"
    : singleAgent && workspace?.processing?.phase === "deferred" ? "deferred"
    : singleAgent && workspace?.processing && ["failed", "uncertain"].includes(workspace.processing.phase) ? "failed"
    : singleAgent && (!workspace?.current || assessmentRefreshRequired || positionsCurrent?.reason) ? "stale"
    : positionsDisplayLoading ? "loading"
    : singleAgent && !positionsCurrent?.commercial && !positionsCurrent?.alternatives?.length ? "empty" : null;
  const positionStatusKey = positionState === "read_failed" ? "workspace.positionsReadFailed"
    : positionState === "uncertain" ? "workflow.phase.uncertain"
    : positionState === "submitting" ? "common.loading"
    : positionState === "processing" || positionState === "failed" || positionState === "deferred" ? `workflow.phase.${workspace?.processing?.phase}`
    : positionState === "stale" ? "workspace.positionsStale"
    : positionState === "empty" ? "workspace.positionsCompleteEmpty" : undefined;
  const eventOccasionFact = facts.find(f => f.fieldPath === "event.event_type");
  // This handler is intentionally not a hook: the loading and error branches above return
  // before request-owned values exist, so adding a hook here would change the hook count when
  // the request finishes loading and crash the workspace on its first authoritative render.
  const reviewPositionFacts = () => {
    if (workspaceEditing || draftDirty) return;
    setActiveReviewTab("facts");
    window.requestAnimationFrame(() => {
      const fields = new Set(positionsCurrent?.resolution?.fact_fields ?? []);
      const rows = document.querySelectorAll<HTMLElement>("[data-field]");
      const target = [...rows].find((row) => fields.has(row.dataset.field ?? ""));
      target?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
    });
  };

  // The server names why a current single-agent draft is not approvable yet; the action bar says
  // so in those terms. Any other processing state keeps the ordinary guidance.
  const processingNext = singleAgent && workspace?.current ? workspace.processing?.next_action : null;
  const taskGate = processingNext === "resolve_tasks" || processingNext === "correct_request" || processingNext === "no_reply" ? processingNext : null;
  // Without a reply a decision is still worth recording: it is what the next processing reads.
  const taskGateDecidable = Boolean(workspace?.tasks.some(task => task.open && task.operator_action === "decide"));
  const openDetails = (target: RequestDetailsTarget) => {
    if (!detailsTarget) detailsReturnRef.current = document.activeElement instanceof HTMLElement
      && document.activeElement.getAttribute("role") !== "menuitem" ? document.activeElement : requestMenuRef.current;
    setDetailsTarget(target);
  };
  const showSolTasks = solHasCurrentResultWithoutDraft(data);
  const showSendChecklist = singleAgent && !showSolTasks && Boolean(draft) && !["sent", "closed"].includes(request.status) && !inTrash;
  const openWorkspaceTasks = () => {
    // An open fact editor must keep its mounted state. Details remain available in that case,
    // and when there is no reply/checklist to navigate to.
    if ((!showSendChecklist && !showSolTasks) || factEditors.size > 0) { openDetails("tasks"); return; }
    setDetailsTarget(null);
    setActiveReviewTab("draft");
    window.requestAnimationFrame(() => {
      const checklist = document.getElementById(showSolTasks ? "current-sol-tasks" : SEND_READINESS_ID);
      checklist?.scrollIntoView({ block: "nearest" }); checklist?.focus({ preventScroll: true });
    });
  };

  const solItems = singleAgent ? solGuidanceItems(data, Boolean(pendingProcess || generating || assessmentRefreshRequired), sendRecoveryState) : [];
  // Explain the same local/server conditions that disable processing, without granting authority.
  const processBlocked = factEditors.size > 0 ? "fact_editor"
    : data.workflow?.recalculation_waiting ? "previous_attempt_finishing"
    : detailRefreshFailed || positionsRead.failed ? "read_failed"
    : assessmentRefreshRequired ? "refresh_pending"
    : preparationStale && !pendingProcess ? "preparation_stale"
    : !data.preparation.can_edit ? data.preparation.reason ?? "unavailable"
    : !data.workflow?.can_process && !workspace?.processing?.in_progress ? "process_unavailable" : null;
  const refusedSolRun = singleAgent && Boolean(data.workflow?.refusal) && !pendingProcess && !generating && !assessmentRefreshRequired;
  const staleSolDraft = !!draft && (draft.is_current_decision !== true || data.incoming_work?.state === "processing");
  const localChanges = workspaceEditing || draftDirty || preparationDirtyRef.current || factEditors.size > 0;
  const readinessLocalIssue: ReadinessLocalIssue | null = localChanges ? "local_changes"
    : detailRefreshFailed || assessmentRefreshRequired || positionsRead.failed || pendingProcess || generating ? "refresh_required"
    : readinessTaskBusy ? "task_busy" : null;
  const navigateGuidance = (target: GuidanceTarget) => {
    // Inspecting a blocker is read-only and must remain possible while an editor owns changes.
    if (target === "tasks") { openWorkspaceTasks(); return; }
    if (target === "processing") { openDetails(target); return; }
    if (localChanges || target === "none") return;
    setDetailsTarget(null);
    if (target === "sources") { onGotoCatalog?.(); return; }
    const showFacts = target === "facts" || target === "conflicts";
    if (showFacts) setOnlyRequiredFacts(true);
    setActiveReviewTab(showFacts ? "facts" : "draft");
    if (target === "questions") { setRevealDraftQuestions(true); return; }
    if (target === "conflicts") {
      // Explicit navigation can reveal again, and consumes the pending automatic reveal.
      if (conflictAttemptId) revealedConflictAttempts.add(conflictAttemptId);
      window.requestAnimationFrame(focusConflictingFact);
      return;
    }
    window.requestAnimationFrame(() => {
      const node = document.querySelector<HTMLElement>(target === "facts" ? "[data-required-open], [role=tabpanel]" : target === "delivery" ? "#sol-delivery, [data-testid=review-actions]" : "[data-testid=draft-message-subject]");
      if (node instanceof HTMLDetailsElement) node.open = true;
      if (node) { node.tabIndex = -1; node.scrollIntoView({ block: "nearest" }); node.focus({ preventScroll: true }); }
    });
  };
  const openAssignment = () => {
    setDetailsTarget(null);
    window.requestAnimationFrame(() => {
      const button = document.querySelector<HTMLButtonElement>("#workspace-assignment button");
      button?.scrollIntoView({ block: "nearest" }); button?.focus(); button?.click();
    });
  };
  const recoveryItem = solItems.find(item => !["none", "draft"].includes(item.target));
  const recoveryTarget = taskGate && workspace?.tasks.length ? "tasks"
    : recoveryItem?.target === "sources" && !onGotoCatalog ? "processing" : recoveryItem?.target ?? "processing";

  const cancelDraftEdit = () => {
    editorGenerationRef.current += 1;
    editorSavedRef.current = null;
    draftEditorSourceRef.current = draftEditorRefreshDraft(draft);
    editorVersionRef.current = draft?.version ?? null;
    editorRequestRevisionRef.current = request.current_revision;
    setEditorStale(false);
    setDraftSubject(draft?.subject || "");
    setRecipientValue(draft?.recipient?.effective_address ?? "");
    setRecipientValid(true);
    setRecipientFocus(false);
    setDraftBody(draft?.reply_body_text ?? draft?.body_text ?? "");
    setDraftBodyHtml(draft?.reply_body_html ?? draft?.body_html ?? "");
    setDraftBodyChanged(false);
    setEditing(false);
  };
  const saveDraftEdit = async () => {
    if (!draft || draftSaving || !recipientValid || editorStale) return false;
    if (!draftDirty) {
      setEditing(false);
      return true;
    }
    setDraftSaving(true);
    const submittedGeneration = editorGenerationRef.current;
    const saved = await act(
      cockpit.editDraft(draft.id, {
        ...(draftSubject !== (draft.subject || "") ? { subject: draftSubject } : {}),
        ...(recipientValue !== (draft.recipient?.effective_address ?? "")
          ? { recipient_addr: recipientValue === draft.recipient?.default_address ? null : recipientValue } : {}),
        ...(draftBodyChanged
          ? draft.signature ? { reply_body_html: draftBodyHtml } : { body_html: draftBodyHtml }
          : {}),
        expected_version: draft.version,
      }).then((result) => { editorSavedRef.current = submittedGeneration; return result; }),
      t("ws.draftSaved"),
    );
    setDraftSaving(false);
    if (saved && editorGenerationRef.current === submittedGeneration) setEditing(false);
    return saved;
  };
  const sendReviewedDraft = async (
    advanceAfterSuccess = false,
    pending?: WorkspaceSubmission,
  ) => {
    if (!pending && (!draft || !draft.outbound_artifact)) return false;
    const storageKey = submissionStorageKey(user?.id ?? "", request.id);
    const submission = pending ?? submissionForPreview(draft!.id, draft!.version,
      request.current_revision, draft!.outbound_artifact!.content_hash,
      submissionRef.current ?? readStoredSubmission(storageKey),
      (data?.send_readiness?.settled_at_send ?? []).flatMap(item => item.task_id ? [item.task_id] : []));
    submissionRef.current = submission;
    storeSubmission(storageKey, submission);
    setSendRecoveryState("awaiting_authority");
    try {
      const result = await cockpit.approveAndSendDraft(submission.draftId, submission.command);
      if (result.status !== "pending") {
        submissionRef.current = null;
        storeSubmission(storageKey, null);
      }
      const sent = result.status === "sent" || result.status === "already_sent";
      if (result.status === "sent") {
        pushToast({ tone: "success", title: t("ws.sentToast") });
      } else if (result.status === "already_sent") {
        pushToast({ tone: "success", title: t("ws.review.alreadySent") });
      } else if (result.status === "needs_reconcile" || result.status === "delivery_uncertain") {
        setSendRecoveryState("uncertain");
        pushToast({ tone: "warning", title: t("ws.review.deliveryUncertain") });
      } else if (result.status === "pending") {
        setSendRecoveryState(null);
        await load({ background: true });
        return false;
      } else {
        pushToast({ tone: "danger", title: t("common.error"), msg:
          ["DRAFT_STALE", "OUTBOUND_ARTIFACT_CHANGED", "OUTBOUND_ARTIFACT_UNAVAILABLE"].includes(result.errorCode ?? "")
            ? t("ws.submissionStale") : result.error || t("ws.review.sendFailed") });
        if (await load({ background: true })) setSendRecoveryState(null);
        return false;
      }
      if (sent && advanceAfterSuccess) {
        const reloaded = await load({ background: true });
        const refreshed = reloaded ? authoritativeDetailRef.current : null;
        if (!refreshed) {
          pushToast({ tone: "warning", title: t("ws.advanceAfterSendFailed") });
          return true;
        }
        // SendService can finish SMTP while preserving new_message when another inbound arrives.
        // Stay on that request unless the authoritative reload says the handled request is done.
        if (
          refreshed.request.lifecycle_status !== "answered"
          && refreshed.request.lifecycle_status !== "done"
        ) return true;
        try {
          // #803: auto-advance may only land on work this operator is on — directly or through a
          // team — so it needs their shifts. One read, right after Mark Done; not a hot path.
          const own = ownPrincipalKeys(
            user?.id,
            await cockpit.listOwnTeamIds().then((r) => r.teamIds).catch(() => []),
          );
          let offset = 0;
          let next: string | null = null;
          do {
            const page = await cockpit.listConversations({ group_by: "request", limit: 100, offset });
            next = nextOpenConversationRoute(
              page.items,
              canonicalIdRef.current ?? requestId,
              own,
            );
            if (next || page.next_offset === null) break;
            offset = page.next_offset;
          } while (true);
          if (next) onOpenRequest(next);
          else onBack();
        } catch {
          pushToast({ tone: "warning", title: t("ws.advanceAfterSendFailed") });
        }
      } else {
        await load({ background: true });
      }
      return true;
    } catch (error: any) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        submissionRef.current = null;
        storeSubmission(storageKey, null);
      }
      if (handleNotAssigned(error)) {
        setSendRecoveryState(null);
        return false;
      }
      if (await handleDecisionSafetyError(error)) {
        setSendRecoveryState(null);
        return false;
      }
      // Only a 4xx response is an authoritative pre-submission refusal. Keep that action
      // fail-closed until the reload succeeds; 5xx/transport failures have an unknown outcome.
      const authoritativeRefusal = error instanceof ApiError
        && error.status >= 400
        && error.status < 500;
      if (!authoritativeRefusal) setSendRecoveryState("uncertain");
      pushToast({ tone: "danger", title: t("common.error"), msg: error instanceof ApiError && (error.data as { code?: string })?.code === "APPROVER_NAME_REQUIRED"
        ? t("ws.approverNameRequiredBody") : error instanceof ApiError && (error.data as { code?: string })?.code === "DRAFT_CLOSING_REGENERATION_REQUIRED"
        ? t("ws.closingRegenerationRequiredBody") : error?.message || String(error) });
      const reloaded = await load({ background: true });
      if (authoritativeRefusal && reloaded) setSendRecoveryState(null);
      return false;
    }
  };

  const approveAndSendReviewedDraft = async () => sendReviewedDraft(true);
  const pendingSubmission = pendingWorkspaceSubmission(data.delivery);

  // One notice model per source; more than one active notice collapses into a single strip.
  const analysis = data.analysis ?? null;
  const newerInboundRevision = newerInboundRevisionPresent({
    lifecycleStatus: request.lifecycle_status,
    currentRequestRevision: request.current_revision,
    decisionRequestRevision: decision?.request_revision,
  });
  const draftOvertakenByNewerMessage = draftWasOvertakenByNewerInbound({
    lifecycleStatus: request.lifecycle_status,
    currentRequestRevision: request.current_revision,
    draft,
  });
  const pipelineNotice = singleAgent ? null : guidance.next_action.code === "RETRY_ANALYSIS"
    ? { key: "extraction-retry", tone: "danger" as const, title: t("ws.extractionFailed"),
        lines: [t("ws.analysis.manualRetryAvailable")] }
    : data.pipeline &&
      ["blocked", "error"].includes(data.pipeline.status) &&
      data.pipeline.reason_code !== "human_review_required"
    ? {
        key: "pipeline",
        tone: data.pipeline.status === "error" ? ("danger" as const) : ("warning" as const),
        title: t(data.pipeline.status === "error" ? "ws.pipeline.errorTitle" : "ws.pipeline.blockedTitle"),
        lines: [
          t(`ws.pipeline.reason.${data.pipeline.reason_code ?? "unknown"}`),
          // #540: say what happens next — a scheduled automatic retry (only while it is
          // still ahead; a passed timestamp would read as a stale promise), or that the
          // bounded budget is spent and the button below is the way forward.
          ...(analysis?.state === "queued" && analysis.reason_code && analysis.retry_after &&
              new Date(analysis.retry_after).getTime() > Date.now()
            ? [t("ws.analysis.retryScheduled", {
                time: new Date(analysis.retry_after).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              })]
            : []),
          ...(analysis?.state === "retryable_failed"
            ? [t("ws.analysis.retriesExhausted")]
            : []),
          // #638: the persisted allowlisted diagnostic — the "why" that used to die with the
          // container logs. Detail is already bounded/scrubbed server-side.
          ...(analysis?.diagnostic
            ? [
              t("ws.analysis.diagnostic", {
                code: t(`ws.analysis.diagnosticCode.${analysis.diagnostic.code}`),
              }) + (analysis.diagnostic.detail ? ` (${analysis.diagnostic.detail})` : ""),
            ]
            : []),
        ],
      }
    : null;
  const reviewReasonNotice = request.human_review_reason
    ? {
        key: "review-reason",
        tone: "warning" as const,
        title: t("ws.replyContextReview.title"),
        lines: [t(
          `ws.replyContextReview.reason.${request.human_review_reason.code}`,
          undefined,
          t("ws.replyContextReview.reason.unknown"),
        )],
      }
    : null;
  // #581/#587: expected missing/newer-revision states use a calm story only when the
  // validated projections agree. Enforcement (blocked actions) is untouched.
  const noticeKind = singleAgent ? null : decisionSafetyNoticeKind({
    decisionPresent: decision !== null,
    decisionActionsBlocked: actionSafety.decisionActionsBlocked,
    safetyEvidenceValid: actionSafety.evidenceValid,
    decisionActionBlockers: actionSafety.actionBlockers,
    guidanceEvidenceValid: guidance.evidenceValid,
    guidanceState: guidance.state,
    guidanceSummaryCode: guidance.summary_code,
    pipelineNoticePresent: pipelineNotice !== null,
    newerInboundRevisionPresent: newerInboundRevision,
    analysisState: analysis?.state ?? null,
  });
  const safetyNotice = noticeKind === "danger_safety"
    ? {
        key: "decision-safety",
        tone: "danger" as const,
        title: t("ws.decisionSafetyTitle"),
        lines: actionSafety.reasonKeys.map((key) => t(key)),
      }
    : null;
  const noDecisionNotice = noticeKind === "info_no_decision"
    ? {
        key: "no-decision",
        tone: "info" as const,
        title: t("ws.noDecisionNoticeTitle"),
        lines: [t("ws.noDecisionNoticeBody")],
      }
    : null;
  const newerMessageNotice = noticeKind === "info_newer_message"
    ? {
        key: "newer-message",
        tone: "info" as const,
        title: t("ws.newerMessageNoticeTitle"),
        lines: [t(newerMessageNoticeBodyKey({
          analysis,
          humanReviewReasonPresent: request.human_review_reason !== null &&
            request.human_review_reason !== undefined,
        }))],
      }
    : null;
  const noticeStack = [
    pipelineNotice,
    reviewReasonNotice,
    safetyNotice,
    noDecisionNotice,
    newerMessageNotice,
  ]
    .filter(Boolean) as Array<{ key: string; tone: "danger" | "warning" | "info"; title: string; lines: string[] }>;
  /**
   * Manual analysis retry (#540): re-arms the exhausted run server-side; the worker tick does
   * the actual work within ~2 minutes, so success here only means "queued again".
   */
  const runAnalysisRetry = async () => {
    setAnalysisRetryBusy(true);
    try {
      await cockpit.retryAnalysis(request.id, request.version ?? 0);
      pushToast({ tone: "success", title: t("ws.analysis.retryQueued") });
      await load({ background: true });
    } catch (error: any) {
      const code = error?.data?.code;
      if (code === "ANALYSIS_RETRY_NOT_AVAILABLE") {
        pushToast({ tone: "warning", title: t("ws.analysis.retryStale") });
        await load({ background: true });
      } else if (code === "FEATURE_DISABLED") {
        pushToast({ tone: "warning", title: t("ws.analysis.retryFeatureOff") });
      } else if (code === "QUOTA_EXCEEDED") {
        pushToast({ tone: "warning", title: error?.message || t("common.error") });
      } else {
        pushToast({ tone: "danger", title: t("common.error"), msg: error?.message || String(error) });
      }
    } finally {
      setAnalysisRetryBusy(false);
    }
  };
  const analysisRetryAction = analysis?.state === "retryable_failed" ? (
    <Button
      variant="secondary"
      size="sm"
      disabled={analysisRetryBusy}
      loading={analysisRetryBusy}
      onClick={() => { void runAnalysisRetry(); }}
    >
      {t("ws.analysis.retryNow")}
    </Button>
  ) : null;
  const actionNotices = noticeStack.length > 1 ? (
    <Banner
      tone={noticeStack.some((notice) => notice.tone === "danger")
        ? "danger"
        : noticeStack.some((notice) => notice.tone === "warning")
          ? "warning"
          : "info"}
      title={t("ws.noticeStackTitle", { n: noticeStack.length })}
      actions={analysisRetryAction}
    >
      <ul style={{ margin: 0, paddingInlineStart: "var(--space-5)" }}>
        {noticeStack.map((notice) => (
          <li key={notice.key}>
            <b>{notice.title}</b>
            {notice.lines.map((line, index) => <span key={index}>{" — "}{line}</span>)}
          </li>
        ))}
      </ul>
    </Banner>
  ) : noticeStack.length === 1 ? (
    <>
      {pipelineNotice && (
        <Banner
          className={reviewStyles.pipelineNotice}
          tone={pipelineNotice.tone}
          title={pipelineNotice.title}
          actions={analysisRetryAction}
        >
          {pipelineNotice.lines.map((line, index) => (
            <span key={index}>{index > 0 ? " — " : ""}{line}</span>
          ))}
        </Banner>
      )}
      {reviewReasonNotice && (
        <Banner tone="warning" title={reviewReasonNotice.title}>
          {reviewReasonNotice.lines[0]}
        </Banner>
      )}
      {safetyNotice && (
        <Banner tone="danger" title={safetyNotice.title}>
          <ul style={{ margin: 0, paddingInlineStart: "var(--space-5)" }}>
            {safetyNotice.lines.map((line, index) => (
              <li key={`${line}:${index}`}>{line}</li>
            ))}
          </ul>
        </Banner>
      )}
      {noDecisionNotice && (
        <Banner tone="info" title={noDecisionNotice.title}>
          {noDecisionNotice.lines[0]}
        </Banner>
      )}
      {newerMessageNotice && (
        <Banner tone="info" title={newerMessageNotice.title}>
          {newerMessageNotice.lines[0]}
        </Banner>
      )}
    </>
  ) : null;

  return (
    <div className={`ws ${reviewStyles.workspacePage}`}>
      <ReplyAcknowledgementEffect
        requestId={request.id}
        token={selectedMessageKey === request.reply_acknowledgement_token
          ? request.reply_acknowledgement_token
          : null}
        acknowledge={acknowledgeRenderedReply}
        reload={reloadAfterReplyAcknowledgement}
        onNotice={handleReplyAcknowledgementNotice}
      />
      <WorkspacePageHeading
        subject={requestSubject}
        company={request.contact_form_source
          ? [formCustomerName || request.confirmed_customer_email, t(request.confirmed_customer_email ? "ws.form.confirmed" : "ws.form.unconfirmed")].filter(Boolean).join(" · ")
          : company}
        requestNumber={requestNumber}
        requestReference={requestReference}
        isTest={request.is_test === true}
        enginePin={request.engine_pin}
        workflowUnavailable={Boolean(request.workflow_unavailable_reason)}
        onBack={() => guardFactEditor(onBack)}
        backToEmailQa={backToEmailQa}
        assignee={<span id="workspace-assignment"><AssigneePicker
          assignees={assignees}
          currentUserId={user?.id}
          canAssign={isAdmin}
          loadOperators={loadOperators}
          loadTeams={loadTeams}
          onAdd={addAssignee}
          onRemove={removeAssignee}
          highlighted={!assignees.length}
        /></span>}
        actions={<>
          {user?.role === "owner" && <WorkflowSwitch data={data} reload={() => load({ background: true })} disabled={isClosed || inTrash || workspaceEditing || factEditors.size > 0 || draftDirty || preparationDirtyRef.current || Boolean(pendingProcess)} />}
          <span className={reviewStyles.headingIconActions}>
            {/* #2180: the heading shows the icon alone; its name appears on hover and focus. */}
            {!isClosed && (
              <span className={reviewStyles.headingTip}>
                <Tooltip content={t("ws.markDone")} placement="bottom">
                  <Button variant="ghost" size="sm" iconOnly aria-label={t("ws.markDone")}
                    data-testid="ws-mark-done" disabled={factEditors.size > 0} onClick={openMarkDone}>
                    <Icon name="fileCheck" size={15} />
                  </Button>
                </Tooltip>
              </span>
            )}
            {!inTrash && typeof request.version === "number" && (
              <span className={reviewStyles.headingTip}>
                <Tooltip content={t("ws.trash.move")} placement="bottom">
                  <Button variant="ghost" size="sm" iconOnly aria-label={t("ws.trash.move")}
                    data-testid="ws-move-to-trash" disabled={factEditors.size > 0} onClick={openTrash}>
                    <Icon name="trash" size={15} />
                  </Button>
                </Tooltip>
              </span>
            )}
            <Menu triggerRef={requestMenuRef} items={[
              { icon: "fileText", label: t("ws.details.title"), onClick: () => openDetails("overview") },
              { icon: "history", label: t("activity.title"), onClick: () => setActivityScope(workspaceKey) },
            ]}
              trigger={<button ref={requestMenuRef} className="iconbtn" type="button" aria-label={t("ws.requestMenu")}>
                <Icon name="more" size={20} />
              </button>} />
          </span>
        </>}
      />

      <Dialog open={retryConfirmation !== null} closeButton={false} title={t("solRefusal.confirmTitle")}
        onClose={() => setRetryConfirmation(null)} initialFocusRef={retryCancelRef} finalFocusRef={retryInvokerRef}
        footer={<><Button ref={retryCancelRef} onClick={() => setRetryConfirmation(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={retryConfirmation !== retryKey || Boolean(processBlocked) || generating || Boolean(workspace?.processing?.in_progress)}
            onClick={() => { if (retryConfirmation === retryKey) void generateDraft(retryConfirmation); }}>{t("workflow.process")}</Button></>}>
        <div className="stack-sm">
          {data.workflow?.refusal ? <p>{t(preparationDirtyRef.current || data.workflow.refusal.input_changed === true ? "solRefusal.inputChanged"
            : data.workflow.refusal.input_changed === false ? "solRefusal.inputUnchanged" : "solRefusal.inputUnknown")}</p>
            : <p>{t("solRefusal.resetNotice")}</p>}
          <p>{t("solRefusal.newCost")}</p>
          <p>{data.workflow?.receipt?.known_micro_usd != null
            ? t("solRefusal.previousCost", { cost: (data.workflow.receipt.known_micro_usd / 1000000).toFixed(4) })
            : t("solRefusal.previousCostUnknown")}</p>
          {retryConfirmation !== retryKey && <p role="alert">{t("solRefusal.confirmChanged")}</p>}
        </div>
      </Dialog>

      {activityScope === workspaceKey && <RequestActivityDialog key={workspaceKey} requestId={request.id}
        onClose={() => setActivityScope(null)} finalFocusRef={requestMenuRef} />}

      <RequestDetailsDialog key={`${workspaceKey}:${historicalData?.request.id ?? "active"}`}
        data={historicalData ?? data} historical={Boolean(historicalData)} target={detailsTarget}
        onClose={() => setDetailsTarget(null)} finalFocusRef={detailsReturnRef} items={solItems}
        localChanges={localChanges} canOpenSources={Boolean(onGotoCatalog)} onNavigate={navigateGuidance}
        onAssignment={isAdmin ? openAssignment : undefined} reload={() => load({ background: true })} />

      {inTrash && (
        <Banner
          tone="warning"
          title={t("ws.trash.bannerTitle")}
          data-testid="ws-trash-banner"
          actions={(
            <Button variant="secondary" size="sm" iconLeft={<Icon name="rotate" size={15} />}
              loading={restoreBusy} disabled={restoreBusy || Boolean(request.message_deletions_pending)} onClick={() => { void restoreRequest(); }}>
              {t("ws.trash.restore")}
            </Button>
          )}
        >
          {t("ws.trash.bannerBody", { date: fmtDate(request.purge_at) })}
          {Boolean(request.message_deletions_pending) && <p>{t("ws.messageDelete.pendingBody")} {t("ws.messageDelete.refreshHint")}</p>}
        </Banner>
      )}

      {/* #711: the send succeeded and the mail still did not arrive. Above the reconciliation
          panel because it is settled fact, not something the operator has to adjudicate. */}
      {!historicalData && data.delivery_failure && (
        <DeliveryFailureBanner failure={data.delivery_failure} />
      )}

      {!historicalData && uncertainDelivery && (
        <div id="sol-delivery"><DeliveryReconciliationPanel
          attempt={uncertainDelivery}
          onResolved={onDeliveryResolved}
          onStale={onDeliveryStale}
        /></div>
      )}

      {historicalData && (
        <section data-testid="previous-request-view" className={reviewStyles.previousRequestView}>
          <div ref={historicalBannerRef} className={reviewStyles.previousRequestBanner}>
            <Banner
              tone="info"
              title={t("ws.previous.readOnlyTitle")}
              actions={(
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setHistoricalData(null)}
                >
                  {t("ws.previous.backToActive")}
                </Button>
              )}
            >
              {t("ws.previous.readOnlyBody")} {historicalData.request.request_no != null
                ? `#${historicalData.request.request_no}`
                : ""}
            </Banner>
          </div>
          <HamburgCutoffStatus
            cutoff={historicalData.decision?.state?.commercial_outcome?.cutoff}
            messages={historicalData.messages}
          />
          <EmailReviewWorkspace
            userId={user?.id ?? null}
            requestId={historicalData.request.id}
            unconfirmedForm={Boolean(historicalData.request.contact_form_source && !historicalData.request.confirmed_customer_email)}
            onOpenRequest={onOpenRequest}
            messages={historicalMessages}
            selectedMessageKey={historicalSelectedMessageKey}
            unreadMessageKey={null}
            onSelectMessage={(key) => {
              setHistoricalSelectedMessageKey(key);
              setHistoricalHoverMark(null);
            }}
            marks={historicalMarks}
            activeMark={historicalActiveMark}
            onMarkHover={setHistoricalHoverMark}
            activeTab={historicalReviewTab}
            onTabChange={setHistoricalReviewTab}
            editing={false}
            panels={{
              draft: (
                <div className={`stack-sm ${reviewStyles.draftTab}`}>
                <DraftPanel
                  extractionFailed={historicalData.review_guidance.summary_code === "EXTRACTION_FAILED"}
                  draft={historicalData.draft}
                  aiOn={flags ? flags.ai_enabled : true}
                  generating={analysisRunInFlight(historicalData.analysis?.state)}
                  analysisStage={historicalData.analysis?.stage ?? null}
                  draftSender={historicalDraftSender}
                  draftRecipient={historicalDraftRecipient}
                  editing={false}
                  subject={historicalData.draft?.subject || ""}
                  body={historicalData.draft?.body_text || ""}
                  bodyHtml={historicalData.draft?.body_html || ""}
                  bodyChanged={false}
                  setSubject={() => undefined}
                  onRichTextChange={() => undefined}
                  marks={historicalDraftMarks}
                  activeMark={historicalActiveMark}
                  onMarkHover={setHistoricalHoverMark}
                  onOpenDetails={() => openDetails("overview")}
                />
                </div>
              ),
              facts: (
                <FactsPanel
                  requestId={historicalData.request.id}
                  requestRevision={historicalData.request.current_revision}
                  facts={historicalFacts}
                  review={historicalData.fact_review}
                  messages={historicalMessages}
                  writerActionsBlocked
                  act={async () => false}
                  onEvidenceSelect={selectHistoricalFactEvidence}
                  onMarkHover={setHistoricalHoverMark}
                />
              ),
              positions: (
                historicalData.request.engine_pin.engine === "single_agent" && historicalData.workflow ? <WorkflowCommercial workflow={historicalData.workflow} /> : <Banner tone="info" title={t("ws.previous.readOnlyTitle")}>
                  {t("ws.previous.noLiveProjection")}
                </Banner>
              ),
              // The historical request has no live customer projection, but its own addresses are
              // part of what it recorded, and the history list is how the operator moves between
              // the requests in the first place.
              client: (
                <div className="stack-sm">
                  <Banner tone="info" title={t("ws.previous.readOnlyTitle")}>
                    {t("ws.previous.noLiveProjection")}
                  </Banner>
                  <CustomerAddresses facts={historicalFacts} />
                  <PreviousRequests
                    items={previousRequests}
                    loading={previousLoading}
                    error={previousError}
                    nextCursor={previousNextCursor}
                    selectedRequestId={historicalData.request.id}
                    selectionLoadingId={historicalLoadingId}
                    disabled={false}
                    onOpen={openPreviousRequestsPanel}
                    onRetry={() => { void loadPreviousRequests(true); }}
                    onLoadMore={() => { void loadMorePreviousRequests(); }}
                    onSelect={(historicalRequestId) => { void openPreviousRequest(historicalRequestId); }}
                  />
                </div>
              ),
            }}
            actions={(
              <div className={reviewStyles.previousRequestFooter}>
                <Icon name="lock" size={14} />
                <span>{t("ws.previous.readOnlyBody")}</span>
              </div>
            )}
          />
        </section>
      )}

      {!historicalData && (
        <HamburgCutoffStatus
          cutoff={decision?.state?.commercial_outcome?.cutoff}
          messages={data.messages}
        />
      )}

      {!historicalData && detailRefreshFailed && <Banner tone="danger" title={t("ws.loadError")}>
        <p>{t("workspace.readFailed")}</p><Button size="sm" onClick={refreshPositions}>{t("workflow.refreshStatus")}</Button>
      </Banner>}
      {!historicalData && <EmailReviewWorkspace
        userId={user?.id ?? null}
        requestId={request.id}
        unconfirmedForm={Boolean(request.contact_form_source && !request.confirmed_customer_email)}
        onOpenRequest={id => guardFactEditor(() => onOpenRequest(id))}
        messages={messages}
        selectedMessageKey={selectedMessageKey}
        unreadMessageKey={request.reply_acknowledgement_token ?? null}
        onSelectMessage={selectMessage}
        messageActions={inTrash ? (message) => <DeleteMessageAction key={`${workspaceKey}:${message.id}`}
          requestId={request.id} version={request.version} message={message} onChanged={(status) => {
            pushToast({ tone: status === "deleted" ? "success" : "warning",
              title: t(status === "deleted" ? "ws.messageDelete.deleted" : "ws.messageDelete.pending") });
            void load({ background: true });
          }} /> : undefined}
        conversationActions={(
          <ThreadOperations
            key={workspaceKey}
            messages={messages}
            requestId={request.id}
            writerActionsBlocked={factEditors.size > 0 || (singleAgent ? isClosed || inTrash || workspaceEditing : actionSafety.writerActionsBlocked)}
            data={data} isOwner={user?.role === "owner"}
            act={act}
          />
        )}
        marks={marks}
        activeMark={activeMark}
        onMarkHover={setHoverMark}
        activeTab={activeReviewTab}
        onTabChange={(tab) => { if (factEditors.size === 0 && (!workspaceEditing || tab === "draft")) setActiveReviewTab(tab); }}
        editing={workspaceEditing || factEditors.size > 0}
        requiredCount={singleAgent && !pendingProcess && !generating && !assessmentRefreshRequired ? workspace?.requirements?.length ?? null : null}
        guidance={singleAgent ? <SolNextStep compact={activeReviewTab === "facts"} items={solItems} conflictFields={activeConflict?.fields} onDetails={() => openDetails("overview")}
          onNavigate={navigateGuidance} localChanges={localChanges} /> : undefined}
        panels={{
          draft: (
            <div className={`stack-sm ${reviewStyles.draftTab}`}>
              {editorStale && <Banner tone="warning" title={t("workflow.editorChanged")}>{t("workflow.editorChangedBody")}
                {!workspaceEditing && <Textarea readOnly value={`${draftSubject}\n${recipientValue}\n\n${draftBody}`} aria-label={t("workflow.unsavedCopy")} />}
                <Button size="sm" onClick={cancelDraftEdit}>{t("workflow.discardLocal")}</Button>
              </Banner>}

            {showSolTasks && <div id="current-sol-tasks" tabIndex={-1}>
              <SolGuidance key={data.workflow!.result_id} data={data} items={solItems} expanded
                onNavigate={navigateGuidance} canOpenSources={Boolean(onGotoCatalog)} onAssignment={isAdmin ? openAssignment : undefined}
                localChanges={localChanges} taskControls={{ disabled: Boolean(readinessLocalIssue || !data.preparation.can_edit
                  || sendRecoveryState || ["SENDING", "UNKNOWN", "IDLE"].includes(data.delivery?.state ?? "")),
                  reload: () => load({ background: true }) }} />
            </div>}
            {staleSolDraft && !showSolTasks && !refusedSolRun && !data.workflow?.recalculation_required && <DraftPanel
              guided={singleAgent} draft={null} aiOn={flags ? flags.ai_enabled : true}
              generating={data.incoming_work?.state === "processing"} incomingState={data.incoming_work}
              processingLabel={singleAgent ? t(`workflow.phase.${data.workflow?.processing.in_progress ? data.workflow.processing.phase : "queued"}`) : undefined}
              analysisStage={singleAgent ? null : analysis?.stage ?? null}
              draftSender={draftSender} draftRecipient={draftRecipient} editing={false}
              subject="" body="" bodyHtml="" bodyChanged={false} setSubject={() => undefined} onRichTextChange={() => undefined} />}
            <SolDraftHistory historical={staleSolDraft} refused={refusedSolRun} editing={workspaceEditing || draftDirty}>
            <DraftPanel
              historicalStatus={staleSolDraft ? (draft?.human_rejected ? "rejected" : "stale") : undefined}
              guided={singleAgent} draft={draft} aiOn={flags ? flags.ai_enabled : true}
              artifactErrorInChecklist={singleAgent && !!draft?.outbound_artifact_error
                && data.send_readiness?.blockers.some(item => item.code === (
                  draft.outbound_artifact_error === "APPROVER_NAME_REQUIRED" ? "approver_name_required"
                    : draft.outbound_artifact_error === "DRAFT_CLOSING_REGENERATION_REQUIRED" ? "closing_regeneration_required"
                    : "artifact_unavailable"))}
              extractionFailed={guidance.summary_code === "EXTRACTION_FAILED"}
              generating={generating || data.incoming_work?.state === "processing"}
              incomingState={data.incoming_work}
              processingLabel={singleAgent ? t(`workflow.phase.${data.workflow?.processing.in_progress ? data.workflow.processing.phase : "queued"}`) : undefined}
              analysisStage={singleAgent ? null : analysis?.stage ?? null}
              onDisplayed={!staleSolDraft && !workspaceEditing && !draftDirty ? acknowledgeDisplayedDraft : undefined}
              draftSender={draftSender} draftRecipient={draftRecipient}
              editing={workspaceEditing} subject={draftSubject} body={draftBody}
              bodyHtml={draftBodyHtml} bodyChanged={draftBodyChanged}
              setSubject={(value) => { editorGenerationRef.current += 1; setDraftSubject(value); }}
              recipientEditor={draft?.recipient && canEditRecipient ? {
                  editing: workspaceEditing, value: recipientValue, valid: recipientValid,
                  busy: draftSaving, focus: recipientFocus,
                  onStart: () => { setActiveReviewTab("draft"); setRecipientFocus(true); setEditing(true); },
                  onChange: (value, valid) => { editorGenerationRef.current += 1; setRecipientValue(value); setRecipientValid(valid); },
                } : undefined}
              onRichTextChange={(value) => {
                // Lexical also republishes unchanged snapshots when this callback is replaced.
                if (value.text !== editorContentRef.current.text || value.html !== editorContentRef.current.html) {
                  editorGenerationRef.current += 1;
                }
                editorContentRef.current = value;
                setDraftBody(value.text);
                setDraftBodyHtml(value.html);
                setDraftBodyChanged(value.changed);
              }}
              overtakenByNewerMessage={draftOvertakenByNewerMessage}
              pendingReviewActionable={guidance.summary_code === "DRAFT_APPROVAL_REQUIRED"}
              marks={draftMarks} activeMark={activeMark} onMarkHover={setHoverMark}
              revealEligible={draftRevealEligible({
                draft,
                historical: false,
                editing: workspaceEditing,
                delivery: data.delivery,
                overtakenByNewerMessage: draftOvertakenByNewerMessage,
              })}
              claimFirstView={claimDraftFirstView}
              onAttachmentsChanged={async () => { await load({ background: true }); }}
              onOpenDetails={staleSolDraft ? undefined : () => openDetails("overview")} />
            </SolDraftHistory>
            {showSendChecklist && <SendReadinessChecklist revealQuestions={revealDraftQuestions} onQuestionsRevealed={questionsRevealed} key={workspaceKey} data={data}
              localIssue={readinessLocalIssue} onBusy={busy => { if (workspaceKeyRef.current === workspaceKey) setReadinessTaskBusy(busy); }} reload={() => load({ background: true })}
              onNavigate={action => {
                if (action === "open_assignment") openAssignment();
                else if (action === "refresh") void load({ background: true });
                else if (action === "open_settings") onGotoSettings?.();
                else navigateGuidance(action === "open_facts" ? "facts" : action === "open_delivery" ? "delivery" : "processing");
              }} />}
            </div>
          ),
          facts: (
            <div className="stack" ref={factsPanelRef}>
              <div>
                <FactsPanel
                  requestId={request.id}
                  requestRevision={request.current_revision}
                  facts={facts}
                  onEditingChange={singleAgent ? trackFactEditing : undefined}
                  requirements={singleAgent ? workspace?.requirements ?? null : undefined}
                  refused={refusedSolRun}
                  historicalRequirements={refusedSolRun ? data.workflow?.historical_requirements : null}
                  conflict={activeConflict}
                  onlyRequired={onlyRequiredFacts}
                  onOnlyRequiredChange={setOnlyRequiredFacts}
                  onOpenQuestionDraft={singleAgent && workspace?.current && workspace.questions.length > 0
                    && draft?.is_current_decision === true && workspace.activeWorkflow?.payload?.draft
                    && !draft.human_rejected && !staleSolDraft && !localChanges
                    ? () => navigateGuidance("draft") : undefined}
                  review={singleAgent ? data.fact_review : null}
                  assessmentPending={Boolean(pendingProcess || generating || assessmentRefreshRequired || workspace?.processing?.in_progress)}
                  assessmentFailed={detailRefreshFailed}
                  onOpenTasks={openWorkspaceTasks}
                  messages={messages}
                  writerActionsBlocked={actionSafety.writerActionsBlocked || assessmentRefreshRequired || detailRefreshFailed || Boolean(pendingProcess || generating || workspace?.processing?.in_progress)}
                  act={act}
                  onEvidenceSelect={selectFactEvidence}
                  onMarkHover={setHoverMark}
                />
              </div>
            </div>
          ),
          positions: (
            <div>{features?.positions_offer === false ? (
            <Banner tone="warning" title={t("ws.positionsUnavailable")}>{t("ws.review.positionsDisabled")}</Banner>
          ) : (
            <div className="stack-sm">
              <PositionsPanel
                positions={positionsCurrent}
                loading={positionsDisplayLoading}
                onRefresh={refreshPositions}
                statusLabel={singleAgent && positionStatusKey ? null : positionStatusKey ? t(positionStatusKey) : undefined}
                statusNotice={singleAgent && ["processing", "failed", "deferred"].includes(positionState ?? "") ? <></> : positionStatusKey && <Banner tone={positionState === "read_failed" || positionState === "failed" ? "danger" : "warning"}
                  title={t(positionStatusKey)}>
                  <p>{t(positionState === "read_failed" ? "workspace.readFailed"
                    : positionState === "uncertain" ? "workspace.recoverSubmissionHint"
                    : positionState === "processing" || positionState === "submitting" ? "workflow.next.wait"
                    : positionState === "failed" || positionState === "deferred" ? `workflow.next.${workspace?.processing?.next_action}`
                    : positionState === "empty" ? "workspace.positionsEmptyBody" : "workspace.positionsStaleBody")}</p>
                  {positionState === "empty" && <>
                    {!!workspace?.requirements?.length && <><ul>{workspace.requirements.map(item => <li key={item.field_path}>
                      {t(`field.${item.field_path}`)}
                    </li>)}</ul><Button size="sm" disabled={localChanges} onClick={() => navigateGuidance("facts")}>{t("solGuide.action.facts")}</Button></>}
                    {!!workspace?.tasks.some(task => task.open) && <Button size="sm" onClick={openWorkspaceTasks}>{t("solGuide.action.tasks")}</Button>}
                    {!!workspace?.questions.length && <Button size="sm" disabled={localChanges} onClick={() => navigateGuidance("draft")}>{t("solGuide.action.draft")}</Button>}
                  </>}
                  {positionState === "failed" && <Button size="sm" onClick={() => openDetails("processing")}>{t("solGuide.action.processing")}</Button>}
                </Banner>}
                eventOccasion={<section aria-label={t("field.event.event_type")}>
                  <FactItem f={eventOccasionFact ?? { fieldPath: "event.event_type", value: null, status: "unknown", isLocked: false }}
                    absent={!eventOccasionFact} requestId={request.id} requestRevision={request.current_revision}
                    messages={messages} writerActionsBlocked={actionSafety.writerActionsBlocked || generating || Boolean(pendingProcess) || preparationDirtyRef.current}
                    act={act} onEditingChange={singleAgent ? trackFactEditing : undefined} onEvidenceSelect={selectFactEvidence} onMarkHover={setHoverMark} />
                  <p className="muted">{t("ws.eventOccasionHint")}</p>
                </section>}
                bpEnabled={flags ? flags.bp_export_enabled : false}
                occasion={positionOccasion}
                onOccasionChange={value => { markPreparationDirty(); setPositionOccasion(value); }}
                preparationDisabled={Boolean(pendingProcess) || preparationStale || detailRefreshFailed || positionsRead.failed || assessmentRefreshRequired || factEditors.size > 0 || Boolean(singleAgent && !data.workflow?.can_process) || !data.preparation.can_edit || generating || Boolean(workspace?.processing?.in_progress) || workspaceEditing || draftDirty}
                onApplyPreparation={() => generateDraft()}
                onSaveMenuOccasion={(occasion, revision) => act(
                  cockpit.saveMenuOccasion(request.id, occasion, revision), t(singleAgent ? "workflow.menuOccasionSaved" : "ws.menuOccasionSaved"),
                )}
                staffingFormat={positionStaffingFormat}
                onStaffingFormatChange={value => { markPreparationDirty(); setPositionStaffingFormat(value); }}
                durationHours={positionDurationHours}
                onDurationHoursChange={value => { markPreparationDirty(); setPositionDurationHours(value); }}
                includeStaffing={positionIncludeStaffing}
                onIncludeStaffingChange={value => { markPreparationDirty(); setPositionIncludeStaffing(value); }}
                onReviewFacts={workspaceEditing || draftDirty ? undefined : reviewPositionFacts}
                technicalPreview={!singleAgent && (
                  <BankettProfiPanel
                    positions={positionsCurrent}
                    loading={positionsDisplayLoading}
                    reference={requestReference}
                  />
                )}
              />
              {processError && <Banner tone="danger" title={t("common.error")}><p>{processError}</p>
                <p>{t("workspace.preparationRetained")}</p><Button size="sm" onClick={refreshPositions}>{t("workflow.refreshStatus")}</Button></Banner>}
              {preparationStale && !pendingProcess && <Banner tone="warning" title={t("workspace.preparationStale")}>
                <Button size="sm" onClick={() => { preparationDirtyRef.current = false; preparationScopeRef.current = null; void load({ background: true }); }}>{t("workflow.discardLocal")}</Button>
              </Banner>}
              {proposalControlsChanged && (
                <Banner tone="warning" title={t("ws.positionsChangedTitle")}>
                  {t(singleAgent ? pendingProcess ? "workspace.preparationRetained" : "workspace.positionsChangedBody" : "ws.positionsChangedBody")}
                </Banner>
              )}
            </div>
          )}</div>
          ),
          // #805: one customer context — who they are, where the invoice and the food go, and
          // what they asked us before. The history list still mounts with the panel, so it is
          // still fetched the first time an operator opens this tab and not before. The audit
          // panel stays admin-only and sits last, out of the operator's reading order.
          client: (
            <div ref={activeCustomerRef} className="stack-sm">
              <ClientPanel key={request.id} request={request} facts={facts} messages={messages}
                customerTypeFact={<FactItem customerPanel
                  f={facts.find((fact) => fact.fieldPath === "customer.type") ?? { fieldPath: "customer.type", value: null, status: "unknown", isLocked: false }}
                  requestId={request.id} requestRevision={request.current_revision} messages={messages}
                  writerActionsBlocked={actionSafety.writerActionsBlocked} act={act}
                  onEditingChange={singleAgent ? trackFactEditing : undefined}
                  onEvidenceSelect={selectFactEvidence} onMarkHover={setHoverMark} />}
                onCustomerConfirmed={async () => { await load({ background: true }); await loadPreviousRequests(true); }} />
              <div>
                <PreviousRequests
                  items={previousRequests}
                  loading={previousLoading}
                  error={previousError}
                  nextCursor={previousNextCursor}
                  selectedRequestId={null}
                  selectionLoadingId={historicalLoadingId}
                  disabled={workspaceEditing}
                  onOpen={openPreviousRequestsPanel}
                  onRetry={() => { void loadPreviousRequests(true); }}
                  onLoadMore={() => { void loadMorePreviousRequests(); }}
                  onSelect={(previousRequestId) => {
                    // Keep the active origin when the operator browses several historical requests.
                    historyReturnRef.current = { requestId, previousRequestId, scrollTop: readPrimaryScrollTop() };
                    void openPreviousRequest(previousRequestId);
                  }}
                />
              </div>
            </div>
          ),
        }}
        actions={(
          <ReviewActions key={workspaceKey}
            draft={showSolTasks && !workspaceEditing && !draftDirty ? null : draft}
            sendReadiness={singleAgent ? data.send_readiness ?? null : undefined}
            sendChecklistVisible={showSendChecklist && activeReviewTab === "draft"}
            factsContext={activeReviewTab === "facts"}
            factEditing={factEditors.size > 0}
            readinessLocalIssue={readinessLocalIssue}
            compactSummary={singleAgent ? t(solItems[0]!.title, solItems[0]!.values) : undefined}
            recoveryAction={singleAgent ? {
              label: t(`solGuide.action.${localChanges ? "processing" : recoveryTarget}`),
              onClick: () => navigateGuidance(localChanges ? "processing" : recoveryTarget),
              // The checklist beside the reply holds every task control: no "Open task" button (#2516).
              hidden: recoveryTarget === "tasks" && (showSendChecklist || showSolTasks && activeReviewTab === "draft") && !localChanges,
            } : undefined}
            workflowProcess={singleAgent ? { recovering: Boolean(pendingProcess), recalculationRequired: data.workflow?.recalculation_required, previousCost: data.workflow?.receipt?.known_micro_usd, enabled: Boolean(!detailRefreshFailed && !positionsRead.failed && !assessmentRefreshRequired && factEditors.size === 0 && (!preparationStale || pendingProcess) && data.workflow?.can_process && data.preparation.can_edit), busy: Boolean(workspace?.processing?.in_progress),
              blocked: processBlocked, onAssign: processBlocked === "REQUEST_NOT_ASSIGNED" && isAdmin ? openAssignment : undefined } : undefined}
            guidance={guidance}
            automaticProcessing={noticeKind === "info_newer_message"}
            onRecompute={recomputeDecision}
            recomputing={recomputing}
            editing={workspaceEditing}
            onStartEdit={() => guardFactEditor(() => { setActiveReviewTab("draft"); setEditing(true); })}
            onSaveEdit={saveDraftEdit}
            onCancelEdit={cancelDraftEdit}
            dirty={draftDirty}
            editInvalid={!recipientValid || editorStale}
            aiOn={flags ? flags.ai_enabled : true}
            generating={draftGenerating}
            analysisInFlight={analysisRunInFlight(analysis?.state)}
            onGenerate={() => generateDraft()}
            proposalControlsChanged={proposalControlsChanged}
            decisionActionsBlocked={actionSafety.decisionActionsBlocked}
            writerActionsBlocked={actionSafety.writerActionsBlocked || factEditors.size > 0}
            onReject={(reason) => draft
              ? act(cockpit.rejectDraft(draft.id, reason), t("ws.draftRejected"))
              : Promise.resolve(false)}
            onApprove={() => draft
              ? act(cockpit.approveDraft(draft.id, draft.version, draft.outbound_artifact?.content_hash ?? ""), t("ws.draftApproved"))
              : Promise.resolve(false)}
            onApproveAndSend={approveAndSendReviewedDraft}
            onSend={sendReviewedDraft}
            pendingSubmissionId={pendingSubmission ? data.delivery?.attemptId : null}
            onContinueSubmission={() => pendingSubmission
              ? sendReviewedDraft(false, pendingSubmission) : Promise.resolve(false)}
            sendRecoveryState={sendRecoveryState}
            onGotoSettings={onGotoSettings}
            positionResolution={workspaceEditing || draftDirty ? null : positionsCurrent?.resolution}
            onReviewFacts={workspaceEditing || draftDirty ? undefined : reviewPositionFacts}
            taskGate={taskGate}
            taskGateReason={workspace?.activeWorkflow?.payload?.outcome.reason}
            taskGateDecidable={taskGateDecidable}
            onOpenTasks={openWorkspaceTasks}
            notices={actionNotices}
          />
        )}
      />}

      <Dialog open={closeOpen} onClose={closeBusy ? undefined : () => setCloseOpen(false)} tone="warning" icon={<Icon name="fileCheck" size={20} />}
        title={t("ws.markDoneTitle")}
        footer={<>
          <Button variant="ghost" disabled={closeBusy} onClick={() => setCloseOpen(false)}>{t("common.cancel")}</Button>
          <Button loading={closeBusy} variant="primary" disabled={closeBusy} onClick={submitMarkDone}>{t("ws.markDoneConfirm")}</Button>
        </>}>
        <div className="stack-sm" style={{ paddingBottom: "var(--space-4)" }}>
          <p style={{ margin: 0 }}>{t("ws.markDoneBody")}</p>
          {closeError && <Banner tone="danger" title={t("common.error")}>{closeError}</Banner>}
          <FormField label={t("ws.markDoneNoteLabel")}>
            <Textarea rows={4} value={closeReason} placeholder={t("ws.markDoneNotePlaceholder")}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setCloseReason(e.target.value); if (closeError) setCloseError(null); }} />
          </FormField>
        </div>
      </Dialog>

      <Dialog open={trashOpen} onClose={trashBusy ? undefined : () => setTrashOpen(false)} tone="danger"
        icon={<Icon name="trash" size={20} />}
        title={t("ws.trash.confirmTitle")}
        footer={<>
          <Button variant="ghost" disabled={trashBusy} onClick={() => setTrashOpen(false)}>{t("common.cancel")}</Button>
          <Button loading={trashBusy} variant="destructive" disabled={trashBusy} onClick={submitTrash}>
            {t("ws.trash.confirm")}
          </Button>
        </>}>
        <div className="stack-sm" style={{ paddingBottom: "var(--space-4)" }}>
          <p style={{ margin: 0 }}>{t("ws.trash.confirmBody", { days: request.trash_retention_days })}</p>
          {trashError && <Banner tone="danger" title={t("common.error")}>{trashError}</Banner>}
        </div>
      </Dialog>

      <Dialog
        open={notAssigned !== null}
        onClose={() => setNotAssigned(null)}
        tone="warning"
        icon={<Icon name="user" size={20} />}
        title={notAssigned ? t("ws.notAssignedTitle") : ""}
        footer={
          <Button variant="primary" onClick={() => setNotAssigned(null)}>
            {t("common.close")}
          </Button>
        }
      >
        <div className="stack-sm" style={{ paddingBottom: "var(--space-4)" }}>
          <p style={{ margin: 0 }}>{t("ws.notAssignedBody")}</p>
        </div>
      </Dialog>

    </div>
  );
}
